import React, { useState, useEffect, useMemo, useCallback, useRef, Fragment } from "react";
import {
  Card,
  Row,
  Col,
  Spin,
  Space,
  Typography,
  Checkbox,
  Button,
  Tag,
  message,
  Tooltip,
  Badge,
  Divider,
  Empty,
  Tabs,
  Progress,
  Table,
  Modal,
  Select,
  Input,
  Upload,
} from "antd";
import {
  SyncOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  DownloadOutlined,
  SearchOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  HolderOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  UploadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  exportToExcel,
  exportDailySalesReportPdf,
  getFilteredRowsFromGrid,
  exportDailySalesDashboardExcel,
} from "./exportUtils";
import AgGridWithColumnState from "../../../../components/AgGridWithColumnState";
import { getDailySalesColumns, defaultColDef } from "./salesDashboardColumns";
import {
  extractApiList,
  normalizeSublyticsOrder,
  normalizeWordPressOrder,
  normalizeAuthNetTransaction,
  normalizeNMITransaction,
  normalizeReliableTransaction,
  normalizeStripeTransaction,
  normalizeTigerTechSale,
  normalizeNexRepSale,
  normalizeRPMOrder,
  normalizeElavonTransaction,
  calculateSalesSummary,
  formatCurrency,
  SOURCE_COLORS,
  enrichSublyticsWithTigerTechMatches,
  enrichSublyticsWithGatewayMatchHint,
  enrichSalesCrossSourceHints,
  enrichRPMWithSublyticsMatches,
  enrichNexRepWithSublyticsMatches,
  isOfflineMethod,
  isSublyticsCash,
  normalizePaymentMethodId,
} from "./salesDashboardUtils";
import {
  fetchSublyticsOrders,
  fetchWordPressOrders,
  fetchTransactions,
  fetchNMITransactions,
  fetchReliableTransactions,
  fetchStripeTransactions,
  fetchTigerTechSalesBatched,
  fetchRPMOrders,
  syncSublyticsOrders,
  syncWordPressOrders,
  syncTransactions,
  syncNMITransactions,
  syncReliableTransactions,
  syncStripeTransactions,
  syncTigerTechSales,
  fetchElavonTransactions,
  startElavonSession,
  submitElavonOtp,
  pollElavonStatus,
  uploadMailOrderTracking,
  fetchMailOrderTracking,
} from "../../../../core/api/data";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const { Text } = Typography;

const DASHBOARD_SOURCE_KEYS = ["sublytics", "wordpress", "authnet", "nmi", "stripe", "reliable", "tigertech", "elavon", "rpm", "nexrep"];
const DASHBOARD_SOURCE_LABELS = {
  sublytics: "Sublytics",
  wordpress: "WordPress",
  authnet: "Auth.net",
  nmi: "NMI",
  stripe: "Stripe",
  reliable: "Reliable Solutions",
  tigertech: "TigerTech",
  elavon: "Elavon",
  rpm: "RPM",
  nexrep: "NexRep",
};

// Axios uses AbortController for timeouts — Chrome shows that as "(canceled)".
const DASHBOARD_FETCH_TIMEOUT_MS = 120000;
// When user explicitly clicks Sync: one request at a time, per-request timeout
const SYNC_REQUEST_TIMEOUT_MS = 30000;

Chart.register(ChartDataLabels);

const SOURCES = [
  { key: "Sublytics", label: "Sublytics", color: SOURCE_COLORS.Sublytics },
  { key: "Wordpress", label: "Wordpress", color: SOURCE_COLORS.Wordpress },
  { key: "TigerTech", label: "TigerTech", color: SOURCE_COLORS.TigerTech },
  { key: "AUTH.NET #1", label: "AUTH.NET #1", color: SOURCE_COLORS["AUTH.NET #1"] },
  { key: "AUTH.NET #2", label: "AUTH.NET #2", color: SOURCE_COLORS["AUTH.NET #2"] },
  { key: "NMI Payarc", label: "NMI Payarc", color: SOURCE_COLORS["NMI Payarc"] },
  { key: "Stripe", label: "Stripe", color: SOURCE_COLORS.Stripe },
  { key: "Reliable Solutions", label: "Reliable Solutions", color: SOURCE_COLORS["Reliable Solutions"] },
  { key: "Cash", label: "Cash", color: SOURCE_COLORS.Cash },
  { key: "Check", label: "Check", color: SOURCE_COLORS.Check },
  { key: "Money Order", label: "Money Order", color: SOURCE_COLORS["Money Order"] },
  { key: "Elavon", label: "Elavon", color: SOURCE_COLORS.Elavon },
  { key: "RPM", label: "RPM", color: SOURCE_COLORS.RPM },
  { key: "NexRep", label: "NexRep", color: SOURCE_COLORS.NexRep },
];

/** Must match `key` built in Tabs from `SOURCES` (lowercase, strip non-alphanumeric). */
const SOURCE_TAB_KEY_TO_SOURCE = Object.fromEntries(
  SOURCES.map((s) => [s.key.toLowerCase().replace(/[^a-z0-9]/g, ""), s.key])
);

/** Headline totals: Website + Sublytics orders only. TigerTech / Auth.Net / NMI are reconciliation layers, not additive. */
const PRIMARY_ORDER_SOURCES = new Set(["Sublytics", "Wordpress"]);

const DEAD_ORDER_STATUSES = new Set(["Declined", "Refunded"]);

/** Order platforms shown on the “Revenue by Platform” bar chart (respects source toggles via `summary`). */
const EXECUTIVE_REVENUE_PLATFORMS = ["Sublytics", "Wordpress", "TigerTech"];

function formatDashboardFetchError(err) {
  if (!err) return "Unknown error";
  const status = err.response?.status;
  const data = err.response?.data;
  let detail;
  if (typeof data === "string") detail = data;
  else if (data?.detail != null) {
    detail = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
  } else if (data?.error != null) {
    detail = typeof data.error === "string" ? data.error : JSON.stringify(data.error);
  } else if (data && typeof data === "object") {
    detail = JSON.stringify(data);
  }
  if (status && detail) return `${status} — ${detail.slice(0, 280)}`;
  if (status) return `HTTP ${status}`;
  if (err.code === "ECONNABORTED" || /timeout/i.test(String(err.message || ""))) {
    return "Timed out (client limit) — backend still running or query too heavy";
  }
  return String(err.message || "Request failed");
}

const DailySalesReport = ({
  brandIds,
  brandNameMap,
  dateRange,
  refreshKey,
  selectedBrands,
  isActive,
}) => {
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState({});
  const [allRecords, setAllRecords] = useState([]);
  const [activeSourceFilters, setActiveSourceFilters] = useState(
    SOURCES.map((s) => s.key)
  );
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sourceFetchStatus, setSourceFetchStatus] = useState(() =>
    DASHBOARD_SOURCE_KEYS.reduce((acc, k) => ({ ...acc, [k]: "idle" }), {})
  );
  const [sourceFetchErrors, setSourceFetchErrors] = useState({});
  const [sourceFetchWarnings, setSourceFetchWarnings] = useState({});

  // Elavon OTP flow state
  const [elavonOtpVisible, setElavonOtpVisible] = useState(false);
  const [elavonSessionId, setElavonSessionId] = useState(null);
  const [elavonOtpValue, setElavonOtpValue] = useState("");
  const [elavonStatus, setElavonStatus] = useState(null);
  const [elavonSubmitting, setElavonSubmitting] = useState(false);
  const elavonPollRef = useRef(null);

  const fetchGenRef = useRef(0);
  const brandIdsRef = useRef(brandIds);
  const dateRangeRef = useRef(dateRange);
  useEffect(() => {
    brandIdsRef.current = brandIds;
  }, [brandIds]);
  useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange]);

  const brandIdsKey = useMemo(
    () => [...brandIds].sort((a, b) => Number(a) - Number(b)).join(","),
    [brandIds]
  );
  const dateRangeKey = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return "";
    return `${dateRange[0].valueOf()}-${dateRange[1].valueOf()}`;
  }, [dateRange]);

  const WIDGET_STORAGE_KEY = "salesDash_widgetOrder";
  const DEFAULT_WIDGET_ORDER = [
    "revenuePlatform", "statusBreakdown", "salesFunnel",
    "revenueBrand", "crossSource", "brandSummary",
  ];
  const [widgetOrder, setWidgetOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(WIDGET_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length === 6) return parsed;
      }
    } catch { /* ignored */ }
    return DEFAULT_WIDGET_ORDER;
  });
  const brandNameMapRef = useRef(brandNameMap);
  useEffect(() => { brandNameMapRef.current = brandNameMap; }, [brandNameMap]);

  const dragItemRef = useRef(null);
  const dragOverItemRef = useRef(null);

  const handleDragStart = useCallback((idx) => { dragItemRef.current = idx; }, []);
  const handleDragEnter = useCallback((idx) => { dragOverItemRef.current = idx; }, []);
  const handleDragEnd = useCallback(() => {
    const from = dragItemRef.current;
    const to = dragOverItemRef.current;
    if (from === null || to === null || from === to) {
      dragItemRef.current = null;
      dragOverItemRef.current = null;
      return;
    }
    setWidgetOrder((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      localStorage.setItem(WIDGET_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
    dragItemRef.current = null;
    dragOverItemRef.current = null;
  }, []);

  // Chart refs
  const revenueChartRef = useRef(null);
  const statusChartRef = useRef(null);
  const clientChartRef = useRef(null);
  const funnelChartRef = useRef(null);
  const revenueChartInstance = useRef(null);
  const statusChartInstance = useRef(null);
  const clientChartInstance = useRef(null);
  const funnelChartInstance = useRef(null);

  const gridRef = useRef(null);
  const rawGridRef = useRef(null);
  const prevActiveRef = useRef(isActive);

  useEffect(() => {
    if (!isActive) {
      prevActiveRef.current = false;
      return;
    }
    if (!prevActiveRef.current) {
      const t = setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
      prevActiveRef.current = true;
      return () => clearTimeout(t);
    }
  }, [isActive]);

  // Sub-tab for individual source views
  const [activeDataTab, setActiveDataTab] = useState("all");

  // Raw data explorer
  const [rawPlatform, setRawPlatform] = useState("Sublytics");

  // Mail order tracking
  const [mailOrderData, setMailOrderData] = useState([]);
  const [mailOrderUploading, setMailOrderUploading] = useState(false);
  const mailOrderGridRef = useRef(null);

  // Column definitions
  const columnDefs = useMemo(() => getDailySalesColumns(), []);

  // ── Raw Data Explorer helpers ──
  const RAW_PLATFORMS = [
    { value: "Sublytics", label: "Sublytics" },
    { value: "Wordpress", label: "WordPress" },
    { value: "TigerTech", label: "TigerTech" },
    { value: "AUTH.NET #1", label: "AUTH.NET #1" },
    { value: "AUTH.NET #2", label: "AUTH.NET #2" },
    { value: "NMI Payarc", label: "NMI Payarc" },
    { value: "Stripe", label: "Stripe" },
    { value: "Reliable Solutions", label: "Reliable Solutions" },
    { value: "Elavon", label: "Elavon" },
    { value: "RPM", label: "RPM" },
    { value: "NexRep", label: "NexRep" },
  ];

  const RAW_COL_DEFS = useMemo(() => {
    const txt = { filter: "agTextColumnFilter", resizable: true, sortable: true };
    const num = { filter: "agNumberColumnFilter", resizable: true, sortable: true, type: "numericColumn" };
    const dt = { filter: "agTextColumnFilter", resizable: true, sortable: true, width: 170 };
    const money = (p) => (p.value != null && p.value !== "" ? `$${parseFloat(p.value).toFixed(2)}` : "");
    const defs = {
      // Raw OLX_Detail line records — every field exactly as the RPM API returns them.
      RPM: [
        { field: "Orderid", headerName: "Order ID", width: 100, ...txt },
        { field: "Client", headerName: "Client", width: 130, ...txt },
        { field: "DateOrdered", headerName: "Date Ordered", width: 120, ...txt },
        { field: "ProdOffer", headerName: "Product / Offer", width: 150, ...txt },
        { field: "ItemID", headerName: "Item ID", width: 100, ...txt },
        { field: "Qty", headerName: "Qty", width: 70, ...num },
        { field: "Price", headerName: "Price", width: 100, ...num, valueFormatter: money },
        { field: "Total", headerName: "Line Total", width: 110, ...num, valueFormatter: money },
        { field: "Shipping", headerName: "Shipping", width: 90, ...num, valueFormatter: money },
        { field: "Tax", headerName: "Tax", width: 80, ...num, valueFormatter: money },
        { field: "BillFirst", headerName: "First Name", width: 120, ...txt },
        { field: "BillLast", headerName: "Last Name", width: 120, ...txt },
        { field: "BillAddress1", headerName: "Address 1", width: 160, ...txt },
        { field: "BillAddress2", headerName: "Address 2", width: 120, ...txt },
        { field: "BillCity", headerName: "City", width: 110, ...txt },
        { field: "BillState", headerName: "State", width: 70, ...txt },
        { field: "BillZip", headerName: "Zip", width: 80, ...txt },
        { field: "BillPhone", headerName: "Phone", width: 120, ...txt },
        { field: "Email", headerName: "Email", width: 200, ...txt },
        { field: "SessionID", headerName: "Session ID", width: 260, ...txt },
        { field: "DNIS", headerName: "DNIS (line)", width: 120, ...txt },
        { field: "Soldby", headerName: "Sold By", width: 90, ...txt },
        { field: "Custom1", headerName: "Custom1", width: 100, ...txt },
        { field: "Custom2", headerName: "Custom2", width: 100, ...txt },
        { field: "Custom3", headerName: "Custom3", width: 100, ...txt },
        { field: "Custom4", headerName: "Custom4", width: 100, ...txt },
        { field: "Custom5", headerName: "Custom5", width: 100, ...txt },
      ],
      Sublytics: [
        { field: "id", headerName: "Order ID", width: 110, ...txt },
        { field: "date_created", headerName: "Date Created", ...dt },
        { field: "date_ordered", headerName: "Date Ordered", ...dt },
        { field: "customer_email", headerName: "Email", width: 200, ...txt },
        { field: "customer_name", headerName: "Customer Name", width: 160, ...txt },
        { field: "grand_total", headerName: "Grand Total", width: 120, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "total_transaction_amount", headerName: "Transaction Amount", width: 140, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "transaction_id", headerName: "Transaction ID", width: 120, ...txt },
        { field: "payment_method", headerName: "Payment Method", width: 150, ...txt },
        { field: "payment_method_id", headerName: "Payment Method ID", width: 150, ...txt },
        { field: "brand_name", headerName: "Brand", width: 130, ...txt },
        { field: "tracking1", headerName: "Source Code", width: 120, ...txt },
        { field: "tracking2", headerName: "Finder", width: 110, ...txt },
      ],
      Wordpress: [
        { field: "id", headerName: "Order ID", width: 100, ...txt },
        { field: "date_created", headerName: "Order Date", ...dt },
        { field: "date_created_gmt", headerName: "Order Date (GMT)", ...dt },
        { field: "status", headerName: "Status", width: 110, ...txt },
        { field: "total", headerName: "Order Total", width: 110, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "payment_method", headerName: "Payment Method", width: 130, ...txt },
        { field: "payment_method_title", headerName: "Payment Title", width: 160, ...txt },
        { field: "transaction_id", headerName: "Transaction ID", width: 200, ...txt },
        { field: "customer_email", headerName: "Email", width: 200, ...txt },
        { field: "customer_name", headerName: "Customer Name", width: 160, ...txt },
        { field: "brand_name", headerName: "Brand", width: 130, ...txt },
      ],
      TigerTech: [
        { field: "order_number", headerName: "Order Number", width: 130, ...txt },
        { field: "customer_number", headerName: "Customer Number", width: 140, ...txt },
        { field: "order_date", headerName: "Order Date", ...dt },
        { field: "bill_first_name", headerName: "First Name", width: 130, ...txt },
        { field: "bill_last_name", headerName: "Last Name", width: 130, ...txt },
        { field: "email", headerName: "Email", width: 200, ...txt },
        { field: "bill_phone_number", headerName: "Phone", width: 130, ...txt },
        { field: "payment_type", headerName: "Payment Type", width: 110, ...txt },
        { field: "product01", headerName: "Product 1", width: 140, ...txt },
        { field: "quantity01", headerName: "Qty 1", width: 70, ...num },
        { field: "price01", headerName: "Price 1", width: 100, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "product02", headerName: "Product 2", width: 140, ...txt },
        { field: "quantity02", headerName: "Qty 2", width: 70, ...num },
        { field: "price02", headerName: "Price 2", width: 100, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "product03", headerName: "Product 3", width: 140, ...txt },
        { field: "merchant_transaction_id", headerName: "Merchant Txn ID", width: 160, ...txt },
        { field: "key_code", headerName: "Key Code", width: 120, ...txt },
        { headerName: "Brand", width: 130, ...txt, valueGetter: (p) => p.data?.brand_name || p.data?.brand?.brand_name || "" },
      ],
      NexRep: [
        { field: "order_number", headerName: "Order Number", width: 130, ...txt },
        { field: "customer_number", headerName: "Customer Number", width: 140, ...txt },
        { field: "order_date", headerName: "Order Date", ...dt },
        { field: "bill_first_name", headerName: "First Name", width: 130, ...txt },
        { field: "bill_last_name", headerName: "Last Name", width: 130, ...txt },
        { field: "email", headerName: "Email", width: 200, ...txt },
        { field: "bill_phone_number", headerName: "Phone", width: 130, ...txt },
        { field: "payment_type", headerName: "Payment Type", width: 110, ...txt },
        { field: "product01", headerName: "Product 1", width: 140, ...txt },
        { field: "quantity01", headerName: "Qty 1", width: 70, ...num },
        { field: "price01", headerName: "Price 1", width: 100, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "product02", headerName: "Product 2", width: 140, ...txt },
        { field: "quantity02", headerName: "Qty 2", width: 70, ...num },
        { field: "price02", headerName: "Price 2", width: 100, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "product03", headerName: "Product 3", width: 140, ...txt },
        { field: "merchant_transaction_id", headerName: "Merchant Txn ID", width: 160, ...txt },
        { field: "key_code", headerName: "Key Code", width: 120, ...txt },
        { headerName: "Brand", width: 130, ...txt, valueGetter: (p) => p.data?.brand_name || p.data?.brand?.brand_name || "" },
      ],
      "AUTH.NET #1": [
        { field: "transId", headerName: "Transaction ID", width: 140, ...txt },
        { field: "invoiceNumber", headerName: "Invoice Number", width: 130, ...txt },
        { field: "transactionStatus", headerName: "Status", width: 150, ...txt },
        { field: "settleAmount", headerName: "Settlement Amount", width: 140, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "submitTimeUTC", headerName: "Submit Time (UTC)", ...dt },
        { field: "submitTimeLocal", headerName: "Submit Time (Local)", ...dt },
        { field: "firstName", headerName: "First Name", width: 130, ...txt },
        { field: "lastName", headerName: "Last Name", width: 130, ...txt },
        { field: "accountType", headerName: "Card Type", width: 100, ...txt },
        { field: "accountNumber", headerName: "Card Number", width: 130, ...txt },
        { field: "marketType", headerName: "Market Type", width: 120, ...txt },
        { field: "product", headerName: "Product", width: 140, ...txt },
        { field: "brand_name", headerName: "Brand", width: 130, ...txt },
        { field: "gateway_account_name", headerName: "Account", width: 160, ...txt },
        { field: "batch", headerName: "Batch ID", width: 110, ...txt },
      ],
      "NMI Payarc": [
        { field: "transaction_id", headerName: "Transaction ID", width: 140, ...txt },
        { field: "transaction_type", headerName: "Type", width: 80, ...txt },
        { field: "condition", headerName: "Status", width: 120, ...txt },
        { field: "order_id", headerName: "Order ID", width: 110, ...txt },
        { field: "action_amount", headerName: "Amount", width: 110, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "action_date", headerName: "Date", ...dt },
        { field: "first_name", headerName: "First Name", width: 120, ...txt },
        { field: "last_name", headerName: "Last Name", width: 120, ...txt },
        { field: "email", headerName: "Email", width: 200, ...txt },
        { field: "phone", headerName: "Phone", width: 120, ...txt },
        { field: "cc_number", headerName: "Card Number", width: 140, ...txt },
        { field: "cc_type", headerName: "Card Type", width: 100, ...txt },
        { field: "action_response_text", headerName: "Response", width: 120, ...txt },
        { field: "action_batch_id", headerName: "Batch ID", width: 110, ...txt },
        { field: "authorization_code", headerName: "Auth Code", width: 110, ...txt },
        { field: "brand_name", headerName: "Brand", width: 130, ...txt },
      ],
      Stripe: [
        { field: "id", headerName: "Charge ID", width: 230, ...txt },
        { field: "created_date", headerName: "Created Date (UTC)", ...dt },
        { headerName: "Amount", width: 110, ...num, valueGetter: (p) => p.data?.amount != null ? (p.data.amount / 100).toFixed(2) : "", valueFormatter: (p) => p.value ? `$${p.value}` : "" },
        { headerName: "Amount Refunded", width: 130, ...num, valueGetter: (p) => p.data?.amount_refunded != null ? (p.data.amount_refunded / 100).toFixed(2) : "0.00", valueFormatter: (p) => `$${p.value}` },
        { field: "currency", headerName: "Currency", width: 80, ...txt },
        { field: "status", headerName: "Status", width: 100, ...txt },
        { field: "description", headerName: "Description", width: 250, ...txt },
        { field: "receipt_email", headerName: "Customer Email", width: 200, ...txt },
        { headerName: "Customer Name", width: 160, ...txt, valueGetter: (p) => p.data?.billing_details?.name || p.data?.metadata?.customer_name || "" },
        { field: "customer_id", headerName: "Customer ID", width: 180, ...txt },
        { field: "order_id", headerName: "Order ID (metadata)", width: 130, ...txt },
        { field: "brand_name", headerName: "Brand", width: 130, ...txt },
      ],
      "Reliable Solutions": [
        { field: "remote_id", headerName: "ID", width: 110, ...txt },
        { field: "created_on", headerName: "Created Date", ...dt },
        { field: "amount", headerName: "Amount", width: 110, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "status", headerName: "Status", width: 100, ...txt },
        { field: "contact_email", headerName: "Email", width: 200, ...txt },
        { field: "contact_name", headerName: "Name", width: 160, ...txt },
        { field: "card_name", headerName: "Card Name", width: 160, ...txt },
        { field: "external_id", headerName: "External ID", width: 130, ...txt },
        { field: "brand_name", headerName: "Brand", width: 130, ...txt },
      ],
      Elavon: [
        { field: "payment_number", headerName: "Payment No.", width: 160, ...txt },
        { field: "payment_date", headerName: "Payment Date", ...dt },
        { field: "transaction_amount", headerName: "Transaction Amount", width: 140, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "payment_amount", headerName: "Payment Amount", width: 140, ...num, valueFormatter: (p) => p.value != null ? `$${parseFloat(p.value).toFixed(2)}` : "" },
        { field: "business_name", headerName: "Business Name", width: 140, ...txt },
        { field: "mid", headerName: "MID", width: 120, ...txt },
        { field: "card_brand", headerName: "Card Brand", width: 100, ...txt },
        { field: "card_number", headerName: "Card Number", width: 160, ...txt },
        { field: "payment_type", headerName: "Payment Type", width: 120, ...txt },
        { field: "authorization_code", headerName: "Auth Code", width: 110, ...txt },
        { field: "settlement_date", headerName: "Settlement Date", ...dt },
        { field: "invoice_number", headerName: "Invoice No.", width: 120, ...txt },
        { field: "interchange_description", headerName: "Interchange Desc.", width: 250, ...txt },
        { field: "gbok_batch_id", headerName: "Batch ID", width: 130, ...txt },
        { field: "brand_name", headerName: "Brand", width: 130, ...txt },
      ],
    };
    defs["AUTH.NET #2"] = defs["AUTH.NET #1"];
    return defs;
  }, []);

  const fetchAllData = useCallback(async () => {
    const brandIds = brandIdsRef.current;
    const dateRange = dateRangeRef.current;
    if (!brandIds.length) {
      setAllRecords([]);
      return;
    }

    const gen = ++fetchGenRef.current;

    const buckets = {
      sublytics: null,
      wordpress: null,
      authnet: null,
      nmi: null,
      stripe: null,
      reliable: null,
      tigertech: null,
      elavon: null,
      rpm: null,
      nexrep: null,
    };

    const sortRows = (rows) => {
      rows.sort((a, b) => {
        if (!a.raw_date) return 1;
        if (!b.raw_date) return -1;
        return new Date(b.raw_date) - new Date(a.raw_date);
      });
      return rows;
    };

    const mergeAll = () => {
      if (fetchGenRef.current !== gen) return;
      const parts = [];
      if (buckets.sublytics !== null) parts.push(...buckets.sublytics);
      if (buckets.wordpress !== null) parts.push(...buckets.wordpress);
      if (buckets.authnet !== null) parts.push(...buckets.authnet);
      if (buckets.nmi !== null) parts.push(...buckets.nmi);
      if (buckets.stripe !== null) parts.push(...buckets.stripe);
      if (buckets.reliable !== null) parts.push(...buckets.reliable);
      if (buckets.tigertech !== null) parts.push(...buckets.tigertech);
      if (buckets.elavon !== null) parts.push(...buckets.elavon);
      if (buckets.rpm !== null) parts.push(...buckets.rpm);
      if (buckets.nexrep !== null) parts.push(...buckets.nexrep);
      setAllRecords(sortRows(parts));
    };

    const markBucket = (key, rows) => {
      if (fetchGenRef.current !== gen) return;
      buckets[key] = rows;
      mergeAll();
    };

    setLoading(true);
    setAllRecords([]);
    setSourceFetchErrors({});
    setSourceFetchStatus(DASHBOARD_SOURCE_KEYS.reduce((acc, k) => ({ ...acc, [k]: "loading" }), {}));

    const filters = {
      brands: brandIds,
      dateRange: dateRange || undefined,
    };
    const tigerTechFilters = { ...filters, source: "TigerTech" };
    const shouldRetryOnce = (e) => {
      if (e?.code === "ECONNABORTED" || e?.code === "ERR_CANCELED" || /timeout/i.test(String(e?.message || ""))) {
        return true;
      }
      const s = e?.response?.status;
      return s === 502 || s === 503 || s === 504;
    };

    const run = async (key, getResponse, normalizeRow) => {
      const reqOpts = { timeout: DASHBOARD_FETCH_TIMEOUT_MS };
      try {
        let res;
        try {
          res = await getResponse(reqOpts);
        } catch (e) {
          if (fetchGenRef.current !== gen) return;
          if (!shouldRetryOnce(e)) throw e;
          await new Promise((r) => setTimeout(r, 750));
          if (fetchGenRef.current !== gen) return;
          res = await getResponse({ timeout: DASHBOARD_FETCH_TIMEOUT_MS });
        }
        if (fetchGenRef.current !== gen) return;
        const normalized = extractApiList(res).map((item) => normalizeRow(item));
        markBucket(key, normalized);
        setSourceFetchStatus((s) =>
          fetchGenRef.current !== gen ? s : { ...s, [key]: "ok" }
        );
        setSourceFetchErrors((prev) => {
          if (fetchGenRef.current !== gen) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        const apiErrors = res?.errors || [];
        if (apiErrors.length) {
          const parts = apiErrors.map((e) => `${e.brand_name}: ${e.error}`);
          setSourceFetchWarnings((prev) =>
            fetchGenRef.current !== gen ? prev : { ...prev, [key]: parts.join("; ") }
          );
        } else {
          setSourceFetchWarnings((prev) => {
            if (fetchGenRef.current !== gen) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          });
        }
      } catch (e) {
        if (fetchGenRef.current !== gen) return;
        markBucket(key, []);
        const msg = formatDashboardFetchError(e);
        setSourceFetchStatus((s) =>
          fetchGenRef.current !== gen ? s : { ...s, [key]: "error" }
        );
        setSourceFetchErrors((prev) =>
          fetchGenRef.current !== gen ? prev : { ...prev, [key]: msg }
        );
        console.warn(`${key} fetch failed:`, e);
      }
    };

    try {
      await Promise.all([
        run("sublytics", (opts) => fetchSublyticsOrders(null, 1, 10000, filters, opts), (item) =>
          normalizeSublyticsOrder(item, brandNameMapRef.current)
        ),
        run("wordpress", (opts) => fetchWordPressOrders(null, filters, opts), (item) =>
          normalizeWordPressOrder(item, brandNameMapRef.current)
        ),
        run("authnet", (opts) => fetchTransactions(null, filters, opts), (item) =>
          normalizeAuthNetTransaction(item, brandNameMapRef.current)
        ),
        run("nmi", (opts) => fetchNMITransactions(null, filters, opts), (item) =>
          normalizeNMITransaction(item, brandNameMapRef.current)
        ),
        run("stripe", (opts) => fetchStripeTransactions(null, filters, opts), (item) =>
          normalizeStripeTransaction(item, brandNameMapRef.current)
        ),
        run("reliable", (opts) => fetchReliableTransactions(null, filters, opts), (item) =>
          normalizeReliableTransaction(item, brandNameMapRef.current)
        ),
        run(
          "tigertech",
          async (opts) => {
            try {
              await Promise.allSettled(
                brandIds.map((id) =>
                  syncTigerTechSales(id, "TigerTech", { timeout: 120000 })
                )
              );
            } catch (_) { /* sync failure is non-fatal; read from DB anyway */ }
            if (fetchGenRef.current !== gen) return [];
            return fetchTigerTechSalesBatched(null, tigerTechFilters, opts, {
              shouldStop: () => fetchGenRef.current !== gen,
            });
          },
          (item) => normalizeTigerTechSale(item, brandNameMapRef.current)
        ),
        run(
          "elavon",
          async (opts) => {
            const elavonFilters = { brand_ids: brandIds };
            if (dateRange) elavonFilters.dateRange = dateRange;
            const res = await fetchElavonTransactions(elavonFilters, opts);
            return res?.results || res || [];
          },
          (item) => normalizeElavonTransaction(item, brandNameMapRef.current)
        ),
        run("rpm", (opts) => fetchRPMOrders(filters, opts), (item) =>
          normalizeRPMOrder(item, brandNameMapRef.current)
        ),
        run(
          "nexrep",
          async (opts) => {
            const nexRepFilters = { ...filters, source: "NexRep" };
            try {
              await Promise.allSettled(
                brandIds.map((id) =>
                  syncTigerTechSales(id, "NexRep", { timeout: 120000 })
                )
              );
            } catch (_) { /* sync failure is non-fatal; read from DB anyway */ }
            if (fetchGenRef.current !== gen) return [];
            return fetchTigerTechSalesBatched(null, nexRepFilters, opts, {
              shouldStop: () => fetchGenRef.current !== gen,
            });
          },
          (item) => normalizeNexRepSale(item, brandNameMapRef.current)
        ),
      ]);
      if (fetchGenRef.current === gen) {
        setLastUpdated(dayjs().format("MMM DD, YYYY HH:mm:ss"));
      }
    } catch (error) {
      console.error("Error fetching sales data:", error);
      message.error("Failed to load sales data");
    } finally {
      if (fetchGenRef.current === gen) {
        setLoading(false);
      }
    }
  }, [brandIdsKey, dateRangeKey, refreshKey]);

  // Fetch data on mount and when dependencies change. No auto-sync — sync only when user explicitly clicks Sync (so the site stays usable).
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Fetch mail order tracking data whenever dateRange changes
  const loadMailOrderData = useCallback(async () => {
    const dr = dateRangeRef.current;
    if (!dr?.[0] || !dr?.[1]) return;
    try {
      const data = await fetchMailOrderTracking(
        dr[0].format("YYYY-MM-DD"),
        dr[1].format("YYYY-MM-DD")
      );
      setMailOrderData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch mail order tracking:", err);
    }
  }, [dateRangeKey]);

  useEffect(() => {
    loadMailOrderData();
  }, [loadMailOrderData]);

  const handleMailOrderUpload = useCallback((file) => {
    setMailOrderUploading(true);
    uploadMailOrderTracking(file)
      .then((res) => {
        message.success(res.message || "Upload complete");
        loadMailOrderData();
      })
      .catch((err) => {
        console.error("Mail order upload failed:", err);
        message.error("Failed to upload mail order file");
      })
      .finally(() => {
        setMailOrderUploading(false);
      });
    return false;
  }, [loadMailOrderData]);

  const moneyOrderSummary = useMemo(() => {
    let totalCount = 0;
    let totalValue = 0;
    mailOrderData.forEach((row) => {
      totalCount += row.money_orders_count || 0;
      totalValue += parseFloat(row.money_orders_value) || 0;
    });
    return { count: totalCount, revenue: totalValue };
  }, [mailOrderData]);

  const _fmtCurrency = (p) => p.value ? `$${parseFloat(p.value).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "$0.00";
  const MAIL_ORDER_COL_DEFS = useMemo(() => [
    { field: "brand", headerName: "Brand", flex: 1, minWidth: 120 },
    { field: "date", headerName: "Date", width: 120 },
    { field: "check_orders_count", headerName: "Check Orders", width: 120, type: "numericColumn" },
    { field: "check_orders_value", headerName: "Check Value", width: 130, type: "numericColumn", valueFormatter: _fmtCurrency },
    { field: "money_orders_count", headerName: "Money Orders", width: 130, type: "numericColumn" },
    { field: "money_orders_value", headerName: "Money Order Value", width: 150, type: "numericColumn", valueFormatter: _fmtCurrency },
    { field: "total_orders", headerName: "Total Orders", width: 120, type: "numericColumn" },
    { field: "total_amount", headerName: "Total Amount", width: 130, type: "numericColumn", valueFormatter: _fmtCurrency },
    { field: "deposit_amount", headerName: "Deposit Amount", width: 140, type: "numericColumn", valueFormatter: _fmtCurrency },
    { field: "undepositable_money_orders", headerName: "Undepositable (MO)", width: 160, type: "numericColumn", valueFormatter: _fmtCurrency },
    { field: "money_orders_sent_to_director", headerName: "MO Sent to Director", width: 170, type: "numericColumn", valueFormatter: _fmtCurrency },
    { field: "do_not_cash_back_orders", headerName: "Do Not Cash (Back Orders)", width: 200, type: "numericColumn", valueFormatter: _fmtCurrency },
  ], []);

  /** Enrich rows using all sources in this load (TT↔order ids, gateway exact match, then cross-source fuzzy links). */
  const displayRecords = useMemo(() => {
    const cashCheckRecords = [];
    allRecords.forEach((r) => {
      if (r.source !== "Sublytics") return;
      const raw = r.rawData || {};
      let pmId = normalizePaymentMethodId(raw.payment_method_id);
      if (!pmId) {
        const txns = Array.isArray(raw.transactions) ? raw.transactions : [];
        for (const tx of txns) {
          const txPmId = normalizePaymentMethodId(tx?.payment_method_id);
          if (txPmId === "check" || txPmId === "cash") { pmId = txPmId; break; }
        }
      }
      const pmName = String(raw.payment_method ?? raw.payment_method_title ?? "").trim().toLowerCase();
      const isCheck = pmId === "check" || pmName.includes("check");
      const isCash = !isCheck && isSublyticsCash(pmId, pmName);
      if (isCheck) {
        cashCheckRecords.push({ ...r, _id: `chk-${r.order_id}`, source: "Check", platform: "Check", matched_gateway: false, matched_tigertech: false });
      } else if (isCash) {
        cashCheckRecords.push({ ...r, _id: `cash-${r.order_id}`, source: "Cash", platform: "Cash", matched_gateway: false, matched_tigertech: false });
      }
    });
    const combined = [...allRecords, ...cashCheckRecords];

    const enriched = enrichNexRepWithSublyticsMatches(
      enrichRPMWithSublyticsMatches(
        enrichSalesCrossSourceHints(
          enrichSublyticsWithGatewayMatchHint(enrichSublyticsWithTigerTechMatches(combined))
        )
      )
    );

    const gwIdToSub = new Map();
    const ttOrderToSub = new Map();
    const recordById = new Map();
    enriched.forEach((r) => { if (r._id) recordById.set(r._id, r); });
    enriched.forEach((r) => {
      if (r.source !== "Sublytics" && r.source !== "Wordpress") return;
      if (r.reconciliation_gateway_id) {
        gwIdToSub.set(r.reconciliation_gateway_id, { order_id: r.order_id, _id: r._id, source: r.source, client: r.client });
      }
      const tt = r.rawData?.respective_tiger_tech_order;
      if (tt) {
        const ttKey = `${r.client || ""}::${String(tt.order_number ?? tt.id ?? "").trim()}`;
        ttOrderToSub.set(ttKey, { order_id: r.order_id, _id: r._id, source: r.source, client: r.client });
      }
    });

    return enriched.map((r) => {
      if (r.source === "Sublytics" || r.source === "Wordpress") {
        if (r.reconciliation_gateway_id) {
          const gwRec = recordById.get(r.reconciliation_gateway_id);
          if (gwRec) return { ...r, _matched_gateway_source: gwRec.source, _matched_gateway_txn_id: gwRec.transaction_id };
        }
        return r;
      }
      const subViaGw = gwIdToSub.get(r._id);
      if (subViaGw) {
        return { ...r, _matched_sub_order_id: subViaGw.order_id, _matched_sub_source: subViaGw.source };
      }
      if (r.source === "TigerTech") {
        const oid = String(r.order_id ?? r.rawData?.order_number ?? "").trim();
        const ttKey = `${r.client || ""}::${oid}`;
        const subViaTT = ttOrderToSub.get(ttKey);
        if (subViaTT) {
          return { ...r, _matched_sub_order_id: subViaTT.order_id, _matched_sub_source: subViaTT.source };
        }
      }
      return r;
    });
  }, [allRecords]);

  // Raw data explorer — depends on displayRecords
  const rawDataRows = useMemo(() => {
    const rows = displayRecords.filter((r) => r.source === rawPlatform);
    // RPM: All-Sources rows are grouped orders, but the Raw Data Explorer should
    // show every raw OLX_Detail line record (all fields) — so expand raw_lines.
    if (rawPlatform === "RPM") {
      return rows.flatMap((r) => {
        const lines = r.rawData?.raw_lines;
        return Array.isArray(lines) && lines.length ? lines : [r.rawData || r];
      });
    }
    return rows.map((r) => r.rawData || r);
  }, [displayRecords, rawPlatform]);

  const rawColDefs = useMemo(() => {
    return RAW_COL_DEFS[rawPlatform] || [];
  }, [rawPlatform, RAW_COL_DEFS]);

  // Filtered records based on source toggles
  const filteredRecords = useMemo(() => {
    return displayRecords.filter((r) => activeSourceFilters.includes(r.source));
  }, [displayRecords, activeSourceFilters]);

  /** Sublytics + Wordpress rows only — used for headline KPIs and brand/revenue charts (not double-counting TT/gateways). */
  const primarySalesRecords = useMemo(
    () => filteredRecords.filter((r) => PRIMARY_ORDER_SOURCES.has(r.source)),
    [filteredRecords]
  );

  /** Same as primarySalesRecords but excludes Declined/Refunded — used for matching widgets, drill-down, brand summary, pie chart. */
  const viableSalesRecords = useMemo(
    () => primarySalesRecords.filter((r) => !DEAD_ORDER_STATUSES.has(r.status)),
    [primarySalesRecords]
  );

  const executiveSummary = useMemo(
    () => calculateSalesSummary(viableSalesRecords),
    [viableSalesRecords]
  );

  // Summary statistics (all selected sources — raw per-source cards, grid, tabs)
  const summary = useMemo(
    () => calculateSalesSummary(filteredRecords),
    [filteredRecords]
  );

  // Status counts + revenue (primary orders only, aligned with funnel / headline)
  const statusCounts = useMemo(() => {
    const counts = {
      Settled: { count: 0, revenue: 0 },
      Pending: { count: 0, revenue: 0 },
      Declined: { count: 0, revenue: 0 },
      Refunded: { count: 0, revenue: 0 },
    };
    primarySalesRecords.forEach((r) => {
      if (counts[r.status] !== undefined) {
        counts[r.status].count++;
        counts[r.status].revenue += r.amount || 0;
      }
    });
    return counts;
  }, [primarySalesRecords]);

  // TigerTech summary
  const tigertechSummary = useMemo(() => {
    const ttRecords = filteredRecords.filter((r) => r.source === "TigerTech");
    return {
      count: ttRecords.length,
      revenue: ttRecords.reduce((sum, r) => sum + (r.amount || 0), 0),
    };
  }, [filteredRecords]);

  // Funnel data: Total Orders (Sublytics+Wordpress) → status breakdown → TigerTech matched (Sublytics only)
  const funnelData = useMemo(() => {
    const orderRecords = filteredRecords.filter(
      (r) => r.source === "Sublytics" || r.source === "Wordpress"
    );
    const total = orderRecords.length;
    const captured = orderRecords.filter((r) =>
      ["Settled", "Captured", "Processed", "Pending"].includes(r.status)
    ).length;
    const settled = orderRecords.filter((r) =>
      ["Settled", "Captured", "Processed"].includes(r.status)
    ).length;
    const pending = orderRecords.filter((r) => r.status === "Pending").length;
    const declined = orderRecords.filter((r) => r.status === "Declined").length;
    const refunded = orderRecords.filter((r) => r.status === "Refunded").length;
    const sublyticsOnly = filteredRecords.filter((r) => r.source === "Sublytics");
    const ttMatched = sublyticsOnly.filter((r) => r.matched_tigertech).length;
    return [
      { label: "Total Orders", value: total, color: "#1890ff" },
      { label: "Captured / Processed", value: captured, color: "#13c2c2" },
      { label: "Settled", value: settled, color: "#52c41a" },
      { label: "Pending", value: pending, color: "#faad14" },
      { label: "Declined", value: declined, color: "#ff4d4f" },
      { label: "Refunded", value: refunded, color: "#1890ff" },
      { label: `TigerTech Matched (${sublyticsOnly.length} Sublytics)`, value: ttMatched, color: "#fa8c16" },
    ];
  }, [filteredRecords]);

  // Cross-matching metrics (Sublytics vs TigerTech / Gateways) — excludes Declined/Refunded
  const matchingMetrics = useMemo(() => {
    const sublyticsRecords = filteredRecords.filter((r) => r.source === "Sublytics" && !DEAD_ORDER_STATUSES.has(r.status));
    const total = sublyticsRecords.length;
    const ttMatched = sublyticsRecords.filter((r) => r.matched_tigertech).length;

    const creditCardOrders = sublyticsRecords.filter((r) => !isOfflineMethod(r));
    const offlineOrders = sublyticsRecords.filter((r) => isOfflineMethod(r));
    const gwMatched = creditCardOrders.filter((r) => r.matched_gateway).length;
    const ccTotal = creditCardOrders.length;
    const offlineCount = offlineOrders.length;

    const byStatus = {};
    creditCardOrders.forEach((r) => {
      const s = r.reconciliation_status || (r.matched_gateway ? "matched" : "missing_in_gateway");
      byStatus[s] = (byStatus[s] || 0) + 1;
    });

    const missingInGateway = byStatus.missing_in_gateway || 0;
    const matchedWithVariance = byStatus.matched_with_variance || 0;
    const ambiguous = byStatus.ambiguous_match || 0;
    const partial = byStatus.partially_matched || 0;
    const duplicateCandidate = byStatus.duplicate_candidate || 0;
    const settledOffline = offlineCount;

    const ccRevenue = creditCardOrders.reduce((s, r) => s + (r.amount || 0), 0);
    const matchedRevenue = creditCardOrders
      .filter((r) => r.matched_gateway)
      .reduce((s, r) => s + (r.amount || 0), 0);
    const offlineRevenue = offlineOrders.reduce((s, r) => s + (r.amount || 0), 0);
    const discrepancyAmount = ccRevenue - matchedRevenue;
    const discrepancyPct = ccRevenue > 0 ? ((discrepancyAmount / ccRevenue) * 100).toFixed(2) : "0.00";

    return {
      totalSublytics: total,
      creditCardTotal: ccTotal,
      offlineTotal: offlineCount,
      offlineRevenue,
      tigertechMatched: ttMatched,
      tigertechPct: total > 0 ? ((ttMatched / total) * 100).toFixed(1) : "0.0",
      gatewayMatched: gwMatched,
      gatewayPct: ccTotal > 0 ? ((gwMatched / ccTotal) * 100).toFixed(1) : "0.0",
      byReconciliationStatus: byStatus,
      missingInGateway,
      matchedWithVariance,
      ambiguous,
      partial,
      duplicateCandidate,
      settledOffline,
      sublyticsRevenue: ccRevenue,
      matchedRevenue,
      discrepancyAmount,
      discrepancyPct,
    };
  }, [filteredRecords]);

  // Client summary table data (Sublytics + Website only)
  const clientSummaryData = useMemo(() => {
    return Object.entries(executiveSummary.byClient || {}).map(([client, data]) => ({
      key: client,
      client,
      orders: data.count,
      revenue: data.revenue,
    }));
  }, [executiveSummary]);

  // --- Hierarchical Drill-Down Data ---
  // State for customer-level matching modal
  const [drillModalVisible, setDrillModalVisible] = useState(false);
  const [drillModalData, setDrillModalData] = useState([]);
  const [drillModalTitle, setDrillModalTitle] = useState("");

  // Level 1: Per-brand row = Sublytics + Website spine, plus TigerTech sale rows (same as PDF right column).
  // Previously we only listed brands that had spine orders and counted TT as "Sublytics rows linked to TT",
  // so TT-only brands (e.g. House of Man, U Power when Sub creds missing) vanished and TT totals did not match the TT card.
  const recordById = useMemo(() => {
    const m = new Map();
    filteredRecords.forEach((r) => { if (r._id) m.set(r._id, r); });
    return m;
  }, [filteredRecords]);

  const drillDownByClient = useMemo(() => {
    const spineRecords = filteredRecords.filter((r) => PRIMARY_ORDER_SOURCES.has(r.source) && !DEAD_ORDER_STATUSES.has(r.status));
    const ttRecords = filteredRecords.filter((r) => r.source === "TigerTech");
    const byClient = {};

    const ensure = (client) => {
      const key = client || "Unknown";
      if (!byClient[key]) {
        byClient[key] = {
          key,
          client: key,
          totalOrders: 0,
          totalRevenue: 0,
          authnetMatched: 0,
          nmiMatched: 0,
          stripeMatched: 0,
          reliableMatched: 0,
          elavonMatched: 0,
          tigertechLinkedFromSub: 0,
          tigerTechRowCount: 0,
          tigerTechRevenue: 0,
          tigerTechRecords: [],
          unmatched: 0,
          unmatchedRevenue: 0,
          orders: [],
        };
      }
      return byClient[key];
    };

    spineRecords.forEach((rec) => {
      const c = ensure(rec.client || "Unknown");
      c.totalOrders++;
      c.totalRevenue += rec.amount || 0;
      c.orders.push(rec);

      const raw = rec.rawData || {};
      let hasAuth = (raw.real_auth_transactions?.length || 0) > 0;
      let hasNMI =
        (raw.real_nmi_transactions?.length || 0) > 0 || !!raw._dashboard_linked_nmi;
      let hasStripe = (raw.real_stripe_transactions?.length || 0) > 0;
      let hasReliable = (raw.real_reliable_transactions?.length || 0) > 0;
      let hasElavon = false;
      const hasTT = !!raw.respective_tiger_tech_order;

      if (!hasAuth && !hasNMI && !hasStripe && !hasReliable && !hasElavon && rec.reconciliation_gateway_id) {
        const gwRec = recordById.get(rec.reconciliation_gateway_id);
        if (gwRec) {
          const src = gwRec.source || "";
          if (src.startsWith("AUTH.NET")) hasAuth = true;
          else if (src === "NMI Payarc") hasNMI = true;
          else if (src === "Stripe") hasStripe = true;
          else if (src === "Reliable Solutions") hasReliable = true;
          else if (src === "Elavon") hasElavon = true;
        }
      }

      if (hasAuth) c.authnetMatched++;
      if (hasNMI) c.nmiMatched++;
      if (hasStripe) c.stripeMatched++;
      if (hasReliable) c.reliableMatched++;
      if (hasElavon) c.elavonMatched++;
      if (hasTT) c.tigertechLinkedFromSub++;

      const rStatus = rec.reconciliation_status || "";
      if (rStatus === "missing_in_gateway" || rStatus === "ambiguous_match" || rStatus === "duplicate_candidate" || (!rec.matched_gateway && rStatus !== "settled_offline")) {
        c.unmatched++;
        c.unmatchedRevenue += rec.amount || 0;
      }
    });

    ttRecords.forEach((rec) => {
      const c = ensure(rec.client || "Unknown");
      c.tigerTechRowCount++;
      c.tigerTechRevenue += rec.amount || 0;
      c.tigerTechRecords.push(rec);
    });

    return Object.values(byClient).sort((a, b) => a.client.localeCompare(b.client));
  }, [filteredRecords, recordById]);

  // Level 2: Gateway breakdown rows for a given client (Auth.Net, NMI, TigerTech)
  const getGatewayBreakdown = useCallback((clientData) => {
    const orders = clientData.totalOrders || 0;
    const gateways = [
      { key: "authnet", label: "AUTH.NET", matched: clientData.authnetMatched, color: "#fa8c16" },
      { key: "nmi", label: "NMI Payarc", matched: clientData.nmiMatched, color: "#eb2f96" },
      { key: "stripe", label: "Stripe", matched: clientData.stripeMatched, color: "#635bff" },
      { key: "reliable", label: "Reliable Solutions", matched: clientData.reliableMatched, color: "#389e0d" },
      { key: "elavon", label: "Elavon", matched: clientData.elavonMatched, color: "#1d4ed8" },
      {
        key: "tigertech",
        label: "TigerTech",
        matched: clientData.tigertechLinkedFromSub,
        color: "#13c2c2",
        ttRowCount: clientData.tigerTechRowCount,
        ttRevenue: clientData.tigerTechRevenue,
      },
    ];
    return gateways
      .filter((gw) => gw.matched > 0 || gw.key === "tigertech" || gw.key === "authnet")
      .map((gw) => ({
        ...gw,
        total: orders,
        pct: orders > 0 ? ((gw.matched / orders) * 100).toFixed(1) : "0.0",
      }));
  }, []);

  // Level 3: Customer-level matching detail for a gateway
  const openCustomerMatching = useCallback((clientData, gatewayKey) => {
    const rows = [];
    const ttKeysFromSub = new Set();

    const gwSourcesForKey = {
      authnet: (s) => s?.startsWith("AUTH.NET"),
      nmi: (s) => s === "NMI Payarc",
      stripe: (s) => s === "Stripe",
      reliable: (s) => s === "Reliable Solutions",
      elavon: (s) => s === "Elavon",
    };

    clientData.orders.forEach((rec) => {
      const raw = rec.rawData || {};
      let matchedTxns = [];

      if (gatewayKey === "authnet") {
        matchedTxns = (raw.real_auth_transactions || []).map((t) => ({
          gateway: "AUTH.NET",
          txn_id: t.transId || "",
          txn_email: t.transaction_details?.customer?.email || t.email || "",
          txn_amount: parseFloat(t.settleAmount || 0),
          txn_status: t.transactionStatus || "",
        }));
      } else if (gatewayKey === "nmi") {
        const fromDb = raw.real_nmi_transactions || [];
        const hints = raw._dashboard_nmi_hints || [];
        const sourceList = fromDb.length ? fromDb : hints;
        matchedTxns = sourceList.map((t) => ({
          gateway: "NMI",
          txn_id: t.transaction_id || "",
          txn_email: t.email || "",
          txn_amount: parseFloat(t.action_amount || 0),
          txn_status: t.condition || "",
        }));
      } else if (gatewayKey === "stripe") {
        matchedTxns = (raw.real_stripe_transactions || []).map((t) => ({
          gateway: "Stripe",
          txn_id: t.id || t.charge_id || "",
          txn_email: t.receipt_email || t.email || "",
          txn_amount: parseFloat(t.amount || 0) / 100,
          txn_status: t.status || "",
        }));
      } else if (gatewayKey === "reliable") {
        matchedTxns = (raw.real_reliable_transactions || []).map((t) => ({
          gateway: "Reliable",
          txn_id: t.remote_id || t.transaction_id || "",
          txn_email: t.email || "",
          txn_amount: parseFloat(t.amount || 0),
          txn_status: t.status || "",
        }));
      } else if (gatewayKey === "elavon") {
        // Elavon matches come from reconciliation_gateway_id (no real_elavon_transactions array on orders)
      } else if (gatewayKey === "tigertech") {
        const tt = raw.respective_tiger_tech_order;
        if (tt) {
          const oid = String(tt.order_number ?? tt.id ?? "").trim();
          if (oid) ttKeysFromSub.add(oid);
          matchedTxns = [{
            gateway: "TigerTech",
            txn_id: tt.order_number || String(tt.id || ""),
            txn_email: tt.email || "",
            txn_amount: [tt.price01, tt.price02, tt.price03, tt.price04, tt.price05]
              .reduce((s, p) => s + (parseFloat(p) || 0), 0),
            txn_status: "Processed",
          }];
        }
      }

      if (matchedTxns.length === 0 && gatewayKey !== "tigertech" && rec.reconciliation_gateway_id) {
        const gwRec = recordById.get(rec.reconciliation_gateway_id);
        const testFn = gwSourcesForKey[gatewayKey];
        if (gwRec && testFn && testFn(gwRec.source)) {
          matchedTxns = [{
            gateway: gwRec.source,
            txn_id: gwRec.transaction_id || gwRec.order_id || "",
            txn_email: gwRec.customer_email || "",
            txn_amount: gwRec.amount || 0,
            txn_status: gwRec.status || "",
          }];
        }
      }

      if (matchedTxns.length > 0) {
        matchedTxns.forEach((txn) => {
          const emailMatch = rec.customer_email && txn.txn_email &&
            rec.customer_email.toLowerCase() === txn.txn_email.toLowerCase();
          const amountMatch = Math.abs((rec.amount || 0) - (txn.txn_amount || 0)) < 0.02;
          rows.push({
            key: `${rec._id}-${txn.txn_id}`,
            sub_order_id: rec.order_id,
            sub_email: rec.customer_email,
            sub_amount: rec.amount,
            sub_name: rec.customer_name,
            ...txn,
            emailMatch,
            amountMatch,
          });
        });
      }
    });

    if (gatewayKey === "tigertech" && Array.isArray(clientData.tigerTechRecords)) {
      clientData.tigerTechRecords.forEach((tr) => {
        const sale = tr.rawData || {};
        const oid = String(tr.order_id ?? sale.order_number ?? sale.id ?? "").trim();
        if (oid && ttKeysFromSub.has(oid)) return;
        const amt = typeof tr.amount === "number" ? tr.amount : parseFloat(tr.amount) || 0;
        rows.push({
          key: `tt-only-${tr._id}`,
          sub_order_id: "—",
          sub_email: tr.customer_email || "",
          sub_amount: null,
          sub_name: tr.customer_name || "",
          gateway: "TigerTech",
          txn_id: oid || String(tr.order_id ?? ""),
          txn_email: tr.customer_email || sale.email || "",
          txn_amount: amt,
          txn_status: "Processed",
          emailMatch: false,
          amountMatch: false,
        });
      });
    }

    const gatewayLabels = { authnet: "AUTH.NET", nmi: "NMI Payarc", stripe: "Stripe", reliable: "Reliable Solutions", tigertech: "TigerTech" };
    setDrillModalTitle(`${clientData.client} — ${gatewayLabels[gatewayKey]} Customer Matching`);
    setDrillModalData(rows);
    setDrillModalVisible(true);
  }, [recordById]);

  // Data filtered by active sub-tab (for individual source views)
  const tabFilteredRecords = useMemo(() => {
    if (activeDataTab === "all") return filteredRecords;
    const sourceName = SOURCE_TAB_KEY_TO_SOURCE[activeDataTab];
    return sourceName
      ? displayRecords.filter((r) => r.source === sourceName)
      : filteredRecords;
  }, [activeDataTab, filteredRecords, displayRecords]);

  // --- Charts ---
  useEffect(() => {
    if (!filteredRecords.length) return;

    const CHART_FONT = { family: "'Inter', 'Segoe UI', sans-serif" };

    // Revenue by Platform — Sublytics, Wordpress, TigerTech (uses `summary` so TT matches the TigerTech card / PDF)
    if (revenueChartRef.current) {
      if (revenueChartInstance.current) revenueChartInstance.current.destroy();
      const platformLabels = EXECUTIVE_REVENUE_PLATFORMS.filter((l) => summary.bySource[l]);
      const revenueData = platformLabels.map((l) => summary.bySource[l].revenue);
      const orderCounts = platformLabels.map((l) => summary.bySource[l].count);
      const colors = platformLabels.map((l) => SOURCE_COLORS[l] || "#999");

      revenueChartInstance.current = new Chart(revenueChartRef.current, {
        type: "bar",
        data: {
          labels: platformLabels.map((l, i) => `${l} (${orderCounts[i]} orders)`),
          datasets: [{
            label: "Revenue ($)",
            data: revenueData,
            backgroundColor: colors.map((c) => `${c}CC`),
            borderColor: colors,
            borderWidth: 1,
            borderRadius: 8,
            borderSkipped: false,
            barPercentage: 0.65,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 20 } },
          plugins: {
            datalabels: {
              anchor: "end",
              align: "top",
              formatter: (val) => formatCurrency(val),
              font: { size: 11, weight: "600", ...CHART_FONT },
              color: "#333",
            },
            legend: { display: false },
            tooltip: {
              backgroundColor: "rgba(0,0,0,0.8)",
              titleFont: { size: 13, ...CHART_FONT },
              bodyFont: { size: 12, ...CHART_FONT },
              padding: 12,
              cornerRadius: 8,
              callbacks: {
                label: (ctx) => ` ${formatCurrency(ctx.raw)}`,
                afterLabel: (ctx) => ` Orders: ${orderCounts[ctx.dataIndex]}`,
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: "#f0f0f0", drawBorder: false },
              ticks: { callback: (v) => formatCurrency(v), font: { size: 11, ...CHART_FONT }, color: "#999" },
              border: { display: false },
            },
            x: {
              grid: { display: false },
              ticks: { font: { size: 11, weight: "500", ...CHART_FONT }, color: "#666" },
              border: { display: false },
            },
          },
        },
      });
    }

    // Status donut chart — primary orders only
    if (statusChartRef.current) {
      if (statusChartInstance.current) statusChartInstance.current.destroy();
      const statusOrder = ["Settled", "Pending", "Declined", "Refunded", "Captured", "Processed"];
      const statusLabels = statusOrder.filter((s) => executiveSummary.byStatus[s]);
      Object.keys(executiveSummary.byStatus).forEach((s) => {
        if (!statusLabels.includes(s)) statusLabels.push(s);
      });
      const statusData = statusLabels.map((l) => executiveSummary.byStatus[l].count);
      const statusRevenue = statusLabels.map((l) => executiveSummary.byStatus[l].revenue);
      const totalAmount = primarySalesRecords.reduce((s, r) => s + (r.amount || 0), 0);
      const statusColors = {
        Settled: "#52c41a", Pending: "#faad14", Declined: "#ff4d4f",
        Refunded: "#1890ff", Captured: "#389e0d", Processed: "#13c2c2", Unknown: "#d9d9d9",
      };

      const centerTextPlugin = {
        id: "statusCenterText",
        afterDraw(chart) {
          const { ctx: c, chartArea } = chart;
          const cx = (chartArea.left + chartArea.right) / 2;
          const cy = (chartArea.top + chartArea.bottom) / 2;
          c.save();
          c.textAlign = "center";
          c.textBaseline = "middle";
          c.font = "bold 14px Inter, sans-serif";
          c.fillStyle = "#333";
          c.fillText(formatCurrency(totalAmount), cx, cy - 8);
          c.font = "11px Inter, sans-serif";
          c.fillStyle = "#999";
          c.fillText("Total", cx, cy + 10);
          c.restore();
        },
      };
      statusChartInstance.current = new Chart(statusChartRef.current, {
        type: "doughnut",
        data: {
          labels: statusLabels,
          datasets: [{
            data: statusData,
            backgroundColor: statusLabels.map((l) => statusColors[l] || "#d9d9d9"),
            borderWidth: 2,
            borderColor: "#fff",
            hoverBorderWidth: 3,
            hoverOffset: 6,
          }],
        },
        plugins: [centerTextPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "62%",
          plugins: {
            datalabels: {
              formatter: (val, ctx) => {
                const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
                return pct > 4 ? `${pct}%` : "";
              },
              color: "#fff",
              font: { size: 12, weight: "700", ...CHART_FONT },
            },
            legend: {
              position: "bottom",
              labels: { padding: 16, usePointStyle: true, pointStyle: "circle", font: { size: 11, ...CHART_FONT } },
            },
            tooltip: {
              backgroundColor: "rgba(0,0,0,0.8)",
              padding: 12,
              cornerRadius: 8,
              bodyFont: { size: 12, ...CHART_FONT },
              callbacks: {
                label: (ctx) => ` ${ctx.label}: ${ctx.raw} orders`,
                afterLabel: (ctx) => ` Revenue: ${formatCurrency(statusRevenue[ctx.dataIndex])}`,
              },
            },
          },
        },
      });
    }

    // Revenue by Brand pie chart — primary orders only
    if (clientChartRef.current && executiveSummary.byClient) {
      if (clientChartInstance.current) clientChartInstance.current.destroy();
      const clientLabels = Object.keys(executiveSummary.byClient);
      const clientData = clientLabels.map((l) => executiveSummary.byClient[l].revenue);
      const brandPalette = [
        "#4361ee", "#3a0ca3", "#7209b7", "#f72585", "#4cc9f0",
        "#06d6a0", "#ffd166", "#ef476f", "#118ab2", "#73d2de",
      ];

      clientChartInstance.current = new Chart(clientChartRef.current, {
        type: "doughnut",
        data: {
          labels: clientLabels,
          datasets: [{
            data: clientData,
            backgroundColor: clientLabels.map((_, i) => brandPalette[i % brandPalette.length]),
            borderWidth: 2,
            borderColor: "#fff",
            hoverBorderWidth: 3,
            hoverOffset: 8,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "55%",
          plugins: {
            datalabels: {
              formatter: (val, ctx) => {
                const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
                return pct > 3 ? `${pct}%` : "";
              },
              color: "#fff",
              font: { size: 12, weight: "700", ...CHART_FONT },
            },
            legend: {
              position: "bottom",
              labels: { padding: 16, usePointStyle: true, pointStyle: "circle", font: { size: 11, ...CHART_FONT } },
            },
            tooltip: {
              backgroundColor: "rgba(0,0,0,0.8)",
              padding: 12,
              cornerRadius: 8,
              callbacks: { label: (ctx) => ` ${ctx.label}: ${formatCurrency(ctx.raw)}` },
              bodyFont: { size: 12, ...CHART_FONT },
            },
          },
        },
      });
    }

    // Sales Funnel (styled horizontal bar)
    if (funnelChartRef.current) {
      if (funnelChartInstance.current) funnelChartInstance.current.destroy();
      const funnelColors = funnelData.map((d) => d.color);
      funnelChartInstance.current = new Chart(funnelChartRef.current, {
        type: "bar",
        data: {
          labels: funnelData.map((d) => d.label),
          datasets: [{
            data: funnelData.map((d) => d.value),
            backgroundColor: funnelColors.map((c) => `${c}CC`),
            borderColor: funnelColors,
            borderWidth: 1,
            borderRadius: 6,
            borderSkipped: false,
            barPercentage: 0.7,
          }],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { right: 60 } },
          plugins: {
            legend: { display: false },
            datalabels: {
              anchor: "end",
              align: "end",
              formatter: (val) => {
                const total = funnelData[0]?.value || 1;
                const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
                return `${val.toLocaleString()} (${pct}%)`;
              },
              font: { size: 11, weight: "600", ...CHART_FONT },
              color: "#444",
            },
            tooltip: {
              backgroundColor: "rgba(0,0,0,0.8)",
              padding: 12,
              cornerRadius: 8,
              bodyFont: { size: 12, ...CHART_FONT },
            },
          },
          scales: {
            x: { beginAtZero: true, grid: { color: "#f0f0f0", drawBorder: false }, ticks: { font: { size: 10, ...CHART_FONT }, color: "#999" }, border: { display: false } },
            y: { grid: { display: false }, ticks: { font: { size: 11, weight: "500", ...CHART_FONT }, color: "#444" }, border: { display: false } },
          },
        },
      });
    }

    return () => {
      if (revenueChartInstance.current) revenueChartInstance.current.destroy();
      if (statusChartInstance.current) statusChartInstance.current.destroy();
      if (clientChartInstance.current) clientChartInstance.current.destroy();
      if (funnelChartInstance.current) funnelChartInstance.current.destroy();
    };
  }, [executiveSummary, summary, funnelData, primarySalesRecords, filteredRecords.length]);

  // ── Elavon interactive OTP sync ──
  const startElavonPoll = useCallback((sid) => {
    if (elavonPollRef.current) clearInterval(elavonPollRef.current);
    elavonPollRef.current = setInterval(async () => {
      try {
        const res = await pollElavonStatus(sid);
        setElavonStatus(res.status);
        if (res.status === "waiting_for_otp") {
          setElavonOtpVisible(true);
        } else if (res.status === "done") {
          clearInterval(elavonPollRef.current);
          elavonPollRef.current = null;
          setElavonOtpVisible(false);
          setSyncLoading((prev) => ({ ...prev, Elavon: false }));
          message.success(`Elavon sync complete — ${res.rows_inserted} transactions imported`);
          fetchAllData();
        } else if (res.status === "error") {
          clearInterval(elavonPollRef.current);
          elavonPollRef.current = null;
          setElavonOtpVisible(false);
          setSyncLoading((prev) => ({ ...prev, Elavon: false }));
          message.error(`Elavon sync failed: ${res.error || "Unknown error"}`);
        }
      } catch {
        // network hiccup, keep polling
      }
    }, 2000);
  }, [fetchAllData]);

  useEffect(() => {
    return () => {
      if (elavonPollRef.current) clearInterval(elavonPollRef.current);
    };
  }, []);

  const handleElavonSync = useCallback(async (forceDownload = false) => {
    setSyncLoading((prev) => ({ ...prev, Elavon: true }));
    setElavonOtpValue("");
    setElavonStatus("starting");
    try {
      const dr = dateRangeRef.current;
      const payload = {};
      if (dr) {
        payload.date_from = dayjs(dr[0]).format("MM-DD-YYYY");
        payload.date_to = dayjs(dr[1]).format("MM-DD-YYYY");
      }
      if (forceDownload) payload.force = true;

      const res = await startElavonSession(payload);

      if (res.status === "already_synced") {
        message.success(`Elavon data already synced (${res.rows_imported} rows). Refreshing...`);
        setSyncLoading((prev) => ({ ...prev, Elavon: false }));
        setElavonStatus(null);
        fetchAllData();
        return;
      }

      message.info("Elavon sync started — logging in...");
      setElavonSessionId(res.session_id);
      startElavonPoll(res.session_id);
    } catch (err) {
      message.error(`Elavon start failed: ${err?.response?.data?.error || err.message}`);
      setElavonOtpVisible(false);
      setSyncLoading((prev) => ({ ...prev, Elavon: false }));
    }
  }, [startElavonPoll, fetchAllData]);

  const handleElavonOtpSubmit = useCallback(async () => {
    if (!elavonOtpValue.trim() || !elavonSessionId) return;
    setElavonSubmitting(true);
    try {
      await submitElavonOtp(elavonSessionId, elavonOtpValue.trim());
      setElavonStatus("submitting_otp");
      message.info("OTP submitted — waiting for Elavon to verify...");
    } catch (err) {
      message.error(`OTP submit failed: ${err?.response?.data?.error || err.message}`);
    } finally {
      setElavonSubmitting(false);
    }
  }, [elavonSessionId, elavonOtpValue]);

  const handleElavonOtpCancel = useCallback(() => {
    if (elavonPollRef.current) {
      clearInterval(elavonPollRef.current);
      elavonPollRef.current = null;
    }
    setElavonOtpVisible(false);
    setElavonSessionId(null);
    setSyncLoading((prev) => ({ ...prev, Elavon: false }));
  }, []);

  // Sync handlers — skip brands that don't have credentials for a given platform
  const handleSync = async (source) => {
    if (source === "Elavon") {
      handleElavonSync();
      return;
    }
    if (!brandIds.length) {
      message.warning("Please select a brand first");
      return;
    }
    setSyncLoading((prev) => ({ ...prev, [source]: true }));
    const syncOpts = { timeout: SYNC_REQUEST_TIMEOUT_MS };
    const syncDate = dateRangeRef.current || undefined;
    try {
      const getTasks = {
        Sublytics: () => brandIds.map((id) => () => syncSublyticsOrders(id, syncOpts, syncDate)),
        Wordpress: () => brandIds.map((id) => () => syncWordPressOrders(id, syncOpts, syncDate)),
        "AUTH.NET #1": () => brandIds.map((id) => () => syncTransactions(id, syncOpts, syncDate)),
        "AUTH.NET #2": () => brandIds.map((id) => () => syncTransactions(id, syncOpts, syncDate)),
        "NMI Payarc": () => brandIds.map((id) => () => syncNMITransactions(id, syncOpts, syncDate)),
        Stripe: () => brandIds.map((id) => () => syncStripeTransactions(id, syncOpts, syncDate)),
        "Reliable Solutions": () => brandIds.map((id) => () => syncReliableTransactions(id, syncOpts, syncDate)),
        TigerTech: () => brandIds.map((id) => () => syncTigerTechSales(id, "TigerTech", syncOpts)),
        NexRep: () => brandIds.map((id) => () => syncTigerTechSales(id, "NexRep", syncOpts)),
      }[source];

      if (getTasks) {
        const tasks = getTasks();
        let succeeded = 0;
        let skipped = 0;
        let failed = 0;
        for (const task of tasks) {
          try {
            const result = await task();
            if (result?.no_credentials) {
              skipped++;
            } else {
              succeeded++;
            }
          } catch {
            failed++;
          }
        }
        if (succeeded > 0) {
          message.success(`${source} sync completed for ${succeeded} brand${succeeded > 1 ? "s" : ""}`);
          fetchAllData();
        }
        if (succeeded === 0 && failed === 0 && skipped > 0) {
          message.warning(`${source}: No credentials configured for selected brands`);
        } else if (skipped > 0 || failed > 0) {
          if (skipped > 0 && failed > 0) {
            message.info(
              `${source}: ${skipped} brand${skipped > 1 ? "s" : ""} skipped (no credentials), ${failed} request(s) failed`
            );
          } else if (skipped > 0) {
            message.info(
              `${source}: ${skipped} brand${skipped > 1 ? "s" : ""} skipped (no credentials for that source)`
            );
          } else {
            message.warning(`${source}: ${failed} brand${failed > 1 ? "s" : ""} failed to sync (check Network tab)`);
          }
        }
      }
    } catch (error) {
      console.error(`Sync error for ${source}:`, error);
      message.error(`Failed to sync ${source}`);
    } finally {
      setSyncLoading((prev) => ({ ...prev, [source]: false }));
    }
  };

  // Export CSV from grid
  const handleExportCSV = useCallback(() => {
    const gridApi = gridRef.current;
    if (gridApi && typeof gridApi.exportDataAsCsv === "function") {
      const dateStr = dayjs().format("YYYY-MM-DD");
      gridApi.exportDataAsCsv({
        fileName: `daily-sales-report-${dateStr}.csv`,
      });
      message.success("CSV exported successfully");
    } else {
      message.warning("Grid not ready for export");
    }
  }, []);

  const handleExportExcel = useCallback(async () => {
    const rows = getFilteredRowsFromGrid(gridRef.current, tabFilteredRecords);
    if (!rows.length) {
      message.warning("No rows to export");
      return;
    }
    try {
      await exportToExcel(rows, columnDefs, "daily-sales-report", gridRef.current);
      message.success("Excel exported successfully");
    } catch (e) {
      console.error(e);
      message.error("Excel export failed");
    }
  }, [tabFilteredRecords, columnDefs]);

  const handleExportPDF = useCallback(async () => {
    const rows = getFilteredRowsFromGrid(gridRef.current, tabFilteredRecords);
    if (!rows.length) {
      message.warning("No rows to export");
      return;
    }
    try {
      await exportDailySalesReportPdf({
        rowData: rows,
        columns: columnDefs,
        fileName: "daily-sales-report",
        gridApi: gridRef.current,
        summaryRecords: filteredRecords,
        dateRange,
        responsible: "",
      });
      message.success("PDF exported successfully");
    } catch (e) {
      console.error(e);
      message.error("PDF export failed");
    }
  }, [tabFilteredRecords, columnDefs, filteredRecords, dateRange]);

  const collectDashboardChartImages = useCallback(() => {
    const pairs = [
      ["Revenue by Platform", revenueChartInstance],
      ["Status Breakdown", statusChartInstance],
      ["Revenue by Brand", clientChartInstance],
      ["Sales Funnel", funnelChartInstance],
    ];
    return pairs
      .map(([title, instRef]) => {
        const chart = instRef.current;
        const canvas = chart?.canvas;
        if (!canvas || typeof canvas.toDataURL !== "function") return null;
        try {
          if (typeof chart.update === "function") {
            chart.update("none");
          }
          return { title, dataUrl: canvas.toDataURL("image/png", 1) };
        } catch {
          return null;
        }
      })
      .filter(Boolean);
  }, []);

  const handleExportDashboard = useCallback(async () => {
    if (!filteredRecords.length) {
      message.warning("No data to export (check Filter by Source or load data)");
      return;
    }
    const chartImages = collectDashboardChartImages();
    await exportDailySalesDashboardExcel(
      filteredRecords,
      dateRange,
      brandNameMap,
      activeDataTab === "all" ? gridRef.current : null,
      chartImages.length ? chartImages : null,
      mailOrderData
    );
    message.success("Dashboard exported to Excel");
  }, [filteredRecords, dateRange, brandNameMap, activeDataTab, collectDashboardChartImages, mailOrderData]);

  const handleExportRawExcel = useCallback(async () => {
    if (!rawDataRows.length) {
      message.warning("No raw data to export");
      return;
    }
    try {
      await exportToExcel(rawDataRows, rawColDefs, `raw-data-${rawPlatform.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`, rawGridRef.current);
      message.success("Raw data exported to Excel");
    } catch (e) {
      console.error("Raw excel export error:", e);
      message.error("Failed to export raw data");
    }
  }, [rawDataRows, rawColDefs, rawPlatform]);

  // Source filter toggle
  const handleSourceToggle = (sourceKey) => {
    setActiveSourceFilters((prev) =>
      prev.includes(sourceKey)
        ? prev.filter((s) => s !== sourceKey)
        : [...prev, sourceKey]
    );
  };

  if (!brandIds.length) {
    return (
      <Empty description="Select a client/brand from the global filter to view daily sales data" />
    );
  }

  return (
    <>
      <div style={{ marginBottom: 8, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {loading && (
          <>
            <Spin size="small" />
            <Text type="secondary" style={{ fontSize: 12 }}>Loading sources…</Text>
          </>
        )}
        <Space size={4} wrap style={{ fontSize: 11 }}>
          {DASHBOARD_SOURCE_KEYS.map((k) => {
            const st = sourceFetchStatus[k];
            if (st === "idle") return null;
            const label = DASHBOARD_SOURCE_LABELS[k];
            if (st === "loading") {
              return (
                <Tag key={k} color="default" style={{ marginInlineEnd: 0 }}>
                  {label}…
                </Tag>
              );
            }
            if (st === "ok") {
              const warn = sourceFetchWarnings[k];
              if (warn) {
                return (
                  <Tooltip key={k} title={warn}>
                    <Tag color="warning" style={{ marginInlineEnd: 0, cursor: "help" }}>
                      {label} (partial)
                    </Tag>
                  </Tooltip>
                );
              }
              return (
                <Tag key={k} color="success" style={{ marginInlineEnd: 0 }}>
                  {label} ✓
                </Tag>
              );
            }
            return (
              <Tooltip key={k} title={sourceFetchErrors[k] || "Request failed — open DevTools → Network for details"}>
                <Tag color="error" style={{ marginInlineEnd: 0, cursor: "help" }}>
                  {label} failed
                </Tag>
              </Tooltip>
            );
          })}
        </Space>
      </div>
      {/* Date info bar */}
      <Card
        size="small"
        bordered={false}
        style={{ marginBottom: 16, borderRadius: 12, background: "linear-gradient(135deg, #f8f9fe 0%, #f4f6fc 100%)", border: "1px solid #e8ecf4" }}
        bodyStyle={{ padding: "12px 20px" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Space size="middle" style={{ fontSize: 13 }}>
            <span>
              <Text style={{ color: "#888", fontSize: 12 }}>Report Generated </Text>
              <Text strong style={{ fontSize: 13 }}>{dayjs().format("MMM DD, YYYY HH:mm")}</Text>
            </span>
            <Divider type="vertical" style={{ height: 20 }} />
            <span>
              <Text style={{ color: "#888", fontSize: 12 }}>Period </Text>
              <Tag color="geekblue" style={{ margin: 0, borderRadius: 6, fontWeight: 500 }}>{dateRange[0]?.format("MMM DD, YYYY")}</Tag>
              <Text style={{ color: "#bbb", margin: "0 4px" }}>&mdash;</Text>
              <Tag color="geekblue" style={{ margin: 0, borderRadius: 6, fontWeight: 500 }}>{dateRange[1]?.format("MMM DD, YYYY")}</Tag>
            </span>
            {lastUpdated && (
              <>
                <Divider type="vertical" style={{ height: 20 }} />
                <Text type="secondary" style={{ fontSize: 11 }}>Updated: {lastUpdated}</Text>
              </>
            )}
          </Space>
          <Space size={6} wrap>
            <Button
              size="small"
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportDashboard}
              style={{ borderRadius: 6, fontWeight: 600 }}
            >
              Export Dashboard
            </Button>
            <Divider type="vertical" style={{ height: 20 }} />
            <Button size="small" icon={<DownloadOutlined />} onClick={handleExportCSV} style={{ borderRadius: 6 }}>CSV</Button>
            <Button size="small" icon={<FileExcelOutlined />} onClick={handleExportExcel} style={{ borderRadius: 6, color: "#52c41a", borderColor: "#b7eb8f" }}>Excel</Button>
            <Button size="small" icon={<FilePdfOutlined />} onClick={handleExportPDF} style={{ borderRadius: 6, color: "#ff4d4f", borderColor: "#ffa39e" }}>PDF</Button>
          </Space>
        </div>
      </Card>

      {/* Summary Statistics */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {[
          { title: "Total Orders", value: executiveSummary.totalOrders, icon: <ShoppingCartOutlined />, color: "#4361ee", bg: "linear-gradient(135deg, #e8edff 0%, #f0f4ff 100%)" },
          { title: "Total Revenue", value: executiveSummary.totalRevenue, icon: <DollarOutlined />, color: "#06d6a0", bg: "linear-gradient(135deg, #e6faf3 0%, #f0fdf8 100%)", isCurrency: true },
          { title: "TigerTech", value: tigertechSummary.count, revenue: tigertechSummary.revenue, icon: <ThunderboltOutlined />, color: "#13c2c2", bg: "linear-gradient(135deg, #e6fffb 0%, #f0fffe 100%)" },
          { title: "Settled", value: statusCounts.Settled.count, revenue: statusCounts.Settled.revenue, icon: <CheckCircleOutlined />, color: "#52c41a", bg: "linear-gradient(135deg, #f0fce8 0%, #f6ffed 100%)" },
          { title: "Pending", value: statusCounts.Pending.count, revenue: statusCounts.Pending.revenue, icon: <ClockCircleOutlined />, color: "#faad14", bg: "linear-gradient(135deg, #fff8e6 0%, #fffbe8 100%)" },
          { title: "Declined", value: statusCounts.Declined.count, revenue: statusCounts.Declined.revenue, icon: <CloseCircleOutlined />, color: "#ff4d4f", bg: "linear-gradient(135deg, #fff1f0 0%, #fff2f0 100%)" },
          { title: "Refunded", value: statusCounts.Refunded.count, revenue: statusCounts.Refunded.revenue, icon: <WarningOutlined />, color: "#7209b7", bg: "linear-gradient(135deg, #f3e8ff 0%, #f9f0ff 100%)" },
        ].map((stat) => (
          <Col key={stat.title} style={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <Card
              size="small"
              bordered={false}
              style={{
                background: stat.bg,
                borderRadius: 12,
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                border: `1px solid ${stat.color}18`,
                flex: 1,
                width: "100%",
              }}
              bodyStyle={{ padding: "14px 16px" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <div style={{ fontSize: 16, color: stat.color, opacity: 0.8 }}>{stat.icon}</div>
                <Text style={{ fontSize: 11, color: "#888", fontWeight: 500 }}>{stat.title}</Text>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: stat.color, lineHeight: 1.2 }}>
                {stat.isCurrency
                  ? formatCurrency(stat.value)
                  : (stat.value || 0).toLocaleString()}
              </div>
              {stat.revenue !== undefined && (
                <div style={{ fontSize: 12, color: "#888", marginTop: 4, fontWeight: 500 }}>
                  {formatCurrency(stat.revenue)}
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      {/* Draggable Chart Widgets */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
          marginBottom: 16,
        }}
      >
        {widgetOrder.map((widgetId, idx) => {
          const widgetConfigs = {
            revenuePlatform: {
              title: "Revenue by Platform (Sublytics + Website + TigerTech)",
              body: (
                <div style={{ height: 300 }}>
                  <canvas ref={revenueChartRef} />
                </div>
              ),
              bodyStyle: { padding: "12px 16px 16px" },
            },
            statusBreakdown: {
              title: "Status Breakdown",
              body: (
                <div style={{ height: 300 }}>
                  <canvas ref={statusChartRef} />
                </div>
              ),
              bodyStyle: { padding: "12px 16px 16px" },
            },
            salesFunnel: {
              title: "Sales Funnel",
              body: (
                <div style={{ height: 350 }}>
                  <canvas ref={funnelChartRef} />
                </div>
              ),
              bodyStyle: { padding: "12px 16px 16px" },
            },
            revenueBrand: {
              title: "Revenue by Brand",
              body: (
                <div style={{ flex: 1, minHeight: 260 }}>
                  <canvas ref={clientChartRef} />
                </div>
              ),
              bodyStyle: { padding: "12px 16px 16px", flex: 1, display: "flex", flexDirection: "column" },
            },
            crossSource: {
              title: "Cross-Source Matching",
              body: (
                <Fragment>
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ padding: "14px 16px", background: "linear-gradient(135deg, #e8fffe 0%, #f0fffd 100%)", borderRadius: 10, border: "1px solid #13c2c218" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <Text strong style={{ fontSize: 13 }}>TigerTech Match</Text>
                        <Text strong style={{ fontSize: 16, color: "#13c2c2" }}>{matchingMetrics.tigertechPct}%</Text>
                      </div>
                      <Progress
                        percent={parseFloat(matchingMetrics.tigertechPct)}
                        strokeColor={{ from: "#13c2c2", to: "#36cfc9" }}
                        trailColor="#e8e8e8"
                        showInfo={false}
                        size="small"
                        style={{ marginBottom: 6 }}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {matchingMetrics.tigertechMatched} of {matchingMetrics.totalSublytics} Sublytics orders
                      </Text>
                    </div>
                  </div>
                  <div>
                    <div style={{ padding: "14px 16px", background: "linear-gradient(135deg, #f0fce8 0%, #f6ffed 100%)", borderRadius: 10, border: "1px solid #52c41a18" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <Text strong style={{ fontSize: 13 }}>Gateway Match</Text>
                        <Text strong style={{ fontSize: 16, color: "#52c41a" }}>{matchingMetrics.gatewayPct}%</Text>
                      </div>
                      <Progress
                        percent={parseFloat(matchingMetrics.gatewayPct)}
                        strokeColor={{ from: "#52c41a", to: "#73d13d" }}
                        trailColor="#e8e8e8"
                        showInfo={false}
                        size="small"
                        style={{ marginBottom: 6 }}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {matchingMetrics.gatewayMatched} of {matchingMetrics.creditCardTotal} credit card orders{" "}
                        (Auth.NET, NMI, Stripe)
                        {matchingMetrics.offlineTotal > 0 && (
                          <span> &middot; {matchingMetrics.offlineTotal} offline (check/cash/MO)</span>
                        )}
                      </Text>
                    </div>
                    {matchingMetrics.totalSublytics > 0 && (
                      <div style={{ marginTop: 10, padding: "10px 14px", background: "#fafafa", borderRadius: 8, fontSize: 11 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <Text type="secondary">Missing in gateway</Text>
                          <Text style={{ color: matchingMetrics.missingInGateway > 0 ? "#ff4d4f" : "#52c41a" }}>{matchingMetrics.missingInGateway}</Text>
                        </div>
                        {matchingMetrics.matchedWithVariance > 0 && (
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <Text type="secondary">Amount variance</Text>
                            <Text style={{ color: "#faad14" }}>{matchingMetrics.matchedWithVariance}</Text>
                          </div>
                        )}
                        {matchingMetrics.partial > 0 && (
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <Text type="secondary">Partially matched</Text>
                            <Text style={{ color: "#faad14" }}>{matchingMetrics.partial}</Text>
                          </div>
                        )}
                        {matchingMetrics.ambiguous > 0 && (
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <Text type="secondary">Ambiguous</Text>
                            <Text style={{ color: "#ff4d4f" }}>{matchingMetrics.ambiguous}</Text>
                          </div>
                        )}
                        {matchingMetrics.settledOffline > 0 && (
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <Text type="secondary">Settled offline</Text>
                            <Text>{matchingMetrics.settledOffline}</Text>
                          </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, paddingTop: 6, borderTop: "1px solid #e8e8e8" }}>
                          <Text type="secondary">Revenue discrepancy</Text>
                          <Tooltip title={`${formatCurrency(matchingMetrics.discrepancyAmount)} unmatched of ${formatCurrency(matchingMetrics.sublyticsRevenue)} total`}>
                            <Text style={{ color: parseFloat(matchingMetrics.discrepancyPct) > 1 ? "#ff4d4f" : "#52c41a", fontWeight: 600 }}>
                              {matchingMetrics.discrepancyPct}% ({formatCurrency(matchingMetrics.discrepancyAmount)})
                            </Text>
                          </Tooltip>
                        </div>
                      </div>
                    )}
                  </div>
                </Fragment>
              ),
              bodyStyle: { padding: "16px 20px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" },
            },
            brandSummary: {
              title: "Brand Summary (Sublytics + Website)",
              body: clientSummaryData.length > 0 ? (
                <div style={{ flex: 1, overflowY: "auto" }}>
                  <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #f0f0f0" }}>
                        <th style={{ textAlign: "left", padding: "10px 16px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Brand</th>
                        <th style={{ textAlign: "right", padding: "10px 16px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Orders</th>
                        <th style={{ textAlign: "right", padding: "10px 16px", color: "#888", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientSummaryData.map((row, ri) => (
                        <tr key={row.key} style={{ borderBottom: "1px solid #f5f5f5", background: ri % 2 === 0 ? "#fafcff" : "#fff" }}>
                          <td style={{ padding: "10px 16px", fontWeight: 500 }}>{row.client}</td>
                          <td style={{ textAlign: "right", padding: "10px 16px", fontWeight: 600 }}>{row.orders.toLocaleString()}</td>
                          <td style={{ textAlign: "right", padding: "10px 16px", fontWeight: 600, color: "#06d6a0" }}>
                            {formatCurrency(row.revenue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: "2px solid #e8e8e8", background: "#f8f9fa" }}>
                        <td style={{ padding: "10px 16px", fontWeight: 700 }}>Total</td>
                        <td style={{ textAlign: "right", padding: "10px 16px", fontWeight: 700 }}>{executiveSummary.totalOrders.toLocaleString()}</td>
                        <td style={{ textAlign: "right", padding: "10px 16px", fontWeight: 700, color: "#06d6a0" }}>{formatCurrency(executiveSummary.totalRevenue)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: 40 }} />
              ),
              bodyStyle: { padding: "8px 0", flex: 1, display: "flex", flexDirection: "column" },
            },
          };

          const cfg = widgetConfigs[widgetId];
          if (!cfg) return null;

          return (
            <div
              key={widgetId}
              draggable
              onDragStart={(e) => {
                handleDragStart(idx);
                e.dataTransfer.effectAllowed = "move";
                e.currentTarget.style.opacity = "0.5";
              }}
              onDragEnter={() => handleDragEnter(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDragEnd={(e) => {
                e.currentTarget.style.opacity = "1";
                handleDragEnd();
              }}
              style={{ display: "flex", cursor: "grab" }}
            >
              <Card
                title={
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <HolderOutlined style={{ color: "#bbb", fontSize: 14 }} />
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{cfg.title}</span>
                  </span>
                }
                size="small"
                bordered={false}
                style={{
                  borderRadius: 12,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
                bodyStyle={cfg.bodyStyle}
              >
                {cfg.body}
              </Card>
            </div>
          );
        })}
      </div>

      {/* Hierarchical Drill-Down: Sublytics → Gateway → Customer Matching */}
      {drillDownByClient.length > 0 && (
        <Card
          size="small"
          bordered={false}
          style={{ marginBottom: 16, borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
          title={
            <Tooltip
              title={
                "Orders / Revenue = Sublytics + Website only. TigerTech column = count of TigerTech sale rows for that brand (same as the TigerTech source card), including sales with no Sublytics row. Auth / NMI counts are still “of spine orders”."
              }
            >
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                Sublytics + Website drill-down &mdash; gateway matching by brand
              </span>
            </Tooltip>
          }
        >
          <Table
            dataSource={drillDownByClient}
            pagination={false}
            size="small"
            rowKey="key"
            expandable={{
              expandedRowRender: (clientRow) => {
                const gateways = getGatewayBreakdown(clientRow);
                return (
                  <Table
                    dataSource={gateways}
                    pagination={false}
                    size="small"
                    rowKey="key"
                    columns={[
                      {
                        title: "Gateway",
                        dataIndex: "label",
                        key: "label",
                        render: (text, row) => <Tag color={row.color}>{text}</Tag>,
                      },
                      {
                        title: "Matched Orders",
                        dataIndex: "matched",
                        key: "matched",
                        align: "right",
                        render: (val, row) => (
                          <span style={{ fontWeight: 600 }}>
                            {val} <Text type="secondary">/ {row.total}</Text>
                            {row.key === "tigertech" && row.ttRowCount > 0 && (
                              <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                                ({row.ttRowCount} TT rows)
                              </Text>
                            )}
                          </span>
                        ),
                      },
                      {
                        title: "Match %",
                        dataIndex: "pct",
                        key: "pct",
                        align: "right",
                        render: (val) => (
                          <Progress
                            percent={parseFloat(val)}
                            size="small"
                            style={{ width: 120, display: "inline-flex" }}
                            format={() => `${val}%`}
                          />
                        ),
                      },
                      {
                        title: "",
                        key: "action",
                        width: 120,
                        render: (_, row) =>
                          row.matched > 0 ||
                          (row.key === "tigertech" && (clientRow.tigerTechRowCount || 0) > 0) ? (
                            <Button
                              type="link"
                              size="small"
                              icon={<SearchOutlined />}
                              onClick={() => openCustomerMatching(clientRow, row.key)}
                            >
                              View Details
                            </Button>
                          ) : null,
                      },
                    ]}
                  />
                );
              },
            }}
            columns={[
              {
                title: "Brand",
                dataIndex: "client",
                key: "client",
                render: (text) => <Text strong>{text}</Text>,
              },
              {
                title: "Orders",
                dataIndex: "totalOrders",
                key: "totalOrders",
                align: "right",
                sorter: (a, b) => a.totalOrders - b.totalOrders,
              },
              {
                title: "Revenue",
                dataIndex: "totalRevenue",
                key: "totalRevenue",
                align: "right",
                render: (val) => (
                  <Text style={{ color: "#52c41a", fontWeight: 600 }}>
                    {formatCurrency(val)}
                  </Text>
                ),
                sorter: (a, b) => a.totalRevenue - b.totalRevenue,
              },
              {
                title: "AUTH.NET",
                dataIndex: "authnetMatched",
                key: "authnet",
                align: "center",
                render: (val, row) => (
                  <Tooltip title={`${val} of ${row.totalOrders} matched`}>
                    <Tag color={val > 0 ? "orange" : "default"}>{val}</Tag>
                  </Tooltip>
                ),
              },
              {
                title: "NMI",
                dataIndex: "nmiMatched",
                key: "nmi",
                align: "center",
                render: (val, row) => (
                  <Tooltip
                    title={`${val} of ${row.totalOrders} — DB-linked or same-brand txn/order id match vs NMI rows in this load`}
                  >
                    <Tag color={val > 0 ? "magenta" : "default"}>{val}</Tag>
                  </Tooltip>
                ),
              },
              {
                title: "Stripe",
                dataIndex: "stripeMatched",
                key: "stripe",
                align: "center",
                render: (val, row) =>
                  val > 0 ? (
                    <Tooltip title={`${val} of ${row.totalOrders} matched via Stripe`}>
                      <Tag color="purple">{val}</Tag>
                    </Tooltip>
                  ) : (
                    <Tag color="default">0</Tag>
                  ),
              },
              {
                title: (
                  <Tooltip title="Settlement verification via Reliable Solutions batch transactions.">
                    <span>Reliable</span>
                  </Tooltip>
                ),
                dataIndex: "reliableMatched",
                key: "reliable",
                align: "center",
                render: (val, row) =>
                  val > 0 ? (
                    <Tooltip title={`${val} of ${row.totalOrders} verified in Reliable Solutions`}>
                      <Tag color="green">{val}</Tag>
                    </Tooltip>
                  ) : (
                    <Tag color="default">0</Tag>
                  ),
              },
              {
                title: (
                  <Tooltip title="Elavon payment transactions matched to Sublytics orders.">
                    <span>Elavon</span>
                  </Tooltip>
                ),
                dataIndex: "elavonMatched",
                key: "elavon",
                align: "center",
                render: (val, row) =>
                  val > 0 ? (
                    <Tooltip title={`${val} of ${row.totalOrders} matched via Elavon`}>
                      <Tag color="blue">{val}</Tag>
                    </Tooltip>
                  ) : (
                    <Tag color="default">0</Tag>
                  ),
              },
              {
                title: (
                  <Tooltip title="TigerTech sale rows for this brand in the current date/source filters (matches the TT card breakdown), not only Sublytics-linked rows.">
                    <span>TigerTech</span>
                  </Tooltip>
                ),
                dataIndex: "tigerTechRowCount",
                key: "tigertech",
                align: "center",
                render: (val, row) => (
                  <Tooltip
                    title={`${val} TT sale row(s), ${formatCurrency(row.tigerTechRevenue || 0)}. Sublytics↔TT linked spine orders: ${row.tigertechLinkedFromSub || 0} of ${row.totalOrders || 0}.`}
                  >
                    <Tag color={val > 0 ? "cyan" : "default"}>{val}</Tag>
                  </Tooltip>
                ),
              },
              {
                title: (
                  <Tooltip title="Sublytics orders missing all gateway links: Auth.NET, NMI, Stripe, Reliable, Elavon, and TigerTech.">
                    <span>Unmatched</span>
                  </Tooltip>
                ),
                dataIndex: "unmatched",
                key: "unmatched",
                align: "center",
                render: (val, row) =>
                  val > 0 ? (
                    <Tooltip
                      title={`No gateway link (Auth, NMI, Stripe, Reliable, Elavon, TigerTech). Unmatched revenue: ${formatCurrency(row.unmatchedRevenue)}`}
                    >
                      <Tag color="red">
                        {val} ({formatCurrency(row.unmatchedRevenue)})
                      </Tag>
                    </Tooltip>
                  ) : (
                    <Tag color="green">0</Tag>
                  ),
              },
            ]}
          />
        </Card>
      )}

      {/* Customer-Level Matching Modal (Level 3 Drill-Down) */}
      <Modal
        title={drillModalTitle}
        open={drillModalVisible}
        onCancel={() => setDrillModalVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
        zIndex={1050}
        styles={{ body: { maxHeight: "70vh", overflow: "auto" } }}
      >
        <Table
          dataSource={drillModalData}
          pagination={{ pageSize: 20 }}
          size="small"
          rowKey="key"
          scroll={{ x: 900 }}
          columns={[
            {
              title: "Sublytics Order",
              dataIndex: "sub_order_id",
              key: "sub_order_id",
              width: 110,
              render: (val) => <Text copyable={{ text: val }}>{val}</Text>,
            },
            {
              title: "Sub. Email",
              dataIndex: "sub_email",
              key: "sub_email",
              width: 170,
              ellipsis: true,
            },
            {
              title: "Sub. Amount",
              dataIndex: "sub_amount",
              key: "sub_amount",
              width: 100,
              align: "right",
              render: (val) => formatCurrency(val),
            },
            {
              title: "Gateway",
              dataIndex: "gateway",
              key: "gateway",
              width: 90,
              render: (val) => <Tag>{val}</Tag>,
            },
            {
              title: "Txn ID",
              dataIndex: "txn_id",
              key: "txn_id",
              width: 130,
              ellipsis: true,
            },
            {
              title: "Txn Email",
              dataIndex: "txn_email",
              key: "txn_email",
              width: 170,
              ellipsis: true,
            },
            {
              title: "Txn Amount",
              dataIndex: "txn_amount",
              key: "txn_amount",
              width: 100,
              align: "right",
              render: (val) => formatCurrency(val),
            },
            {
              title: "Email Match",
              dataIndex: "emailMatch",
              key: "emailMatch",
              width: 90,
              align: "center",
              render: (val) =>
                val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
            },
            {
              title: "Amt Match",
              dataIndex: "amountMatch",
              key: "amountMatch",
              width: 90,
              align: "center",
              render: (val) =>
                val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
            },
          ]}
          summary={(pageData) => {
            const emailMatches = pageData.filter((r) => r.emailMatch).length;
            const amtMatches = pageData.filter((r) => r.amountMatch).length;
            return (
                <Table.Summary.Row style={{ background: "#fafafa", fontWeight: 600 }}>
                <Table.Summary.Cell index={0} colSpan={7}>
                  Page: {pageData.length} gateway row{pageData.length === 1 ? "" : "s"} (email/amount columns compare Sublytics vs gateway)
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="center">
                  <Tag color={emailMatches === pageData.length ? "green" : "orange"}>
                    {emailMatches}/{pageData.length}
                  </Tag>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="center">
                  <Tag color={amtMatches === pageData.length ? "green" : "orange"}>
                    {amtMatches}/{pageData.length}
                  </Tag>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Modal>

      {/* Source per-platform breakdown — 3x2 grid */}
      <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
        {SOURCES.map((src) => {
          const srcData = src.key === "Money Order"
            ? moneyOrderSummary
            : (summary.bySource[src.key] || { count: 0, revenue: 0 });
          return (
            <Col span={8} key={src.key} style={{ display: "flex" }}>
              <Card
                size="small"
                bordered={false}
                style={{
                  borderRadius: 12,
                  background: `linear-gradient(135deg, ${src.color}0A 0%, ${src.color}04 100%)`,
                  border: `1px solid ${src.color}20`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  flex: 1,
                }}
                bodyStyle={{ padding: "16px 20px" }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <Tag
                      color={src.color}
                      style={{ fontSize: 12, padding: "2px 12px", borderRadius: 10, fontWeight: 600, marginBottom: 10, display: "inline-block" }}
                    >
                      {src.label}
                    </Tag>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span style={{ fontSize: 26, fontWeight: 700, color: "#1a1a2e", lineHeight: 1.2 }}>
                        {srcData.count.toLocaleString()}
                      </span>
                      <span style={{ fontSize: 12, color: "#999" }}>orders</span>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>Revenue</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: src.color }}>
                      {formatCurrency(srcData.revenue)}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Source Filters + Sync Buttons + Grid with Sub-Tabs */}
      <Card
        title={<span style={{ fontSize: 15, fontWeight: 600 }}>Sales Data</span>}
        size="small"
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "visible" }}
        styles={{ body: { overflow: "visible" } }}
        extra={
          <Space size={6} wrap>
            {SOURCES.map((src) => (
              <Tooltip key={src.key} title={`Sync ${src.label}`}>
                <Button
                  size="small"
                  icon={<SyncOutlined spin={syncLoading[src.key]} />}
                  onClick={() => handleSync(src.key)}
                  loading={syncLoading[src.key]}
                  style={{ borderColor: `${src.color}66`, color: src.color, borderRadius: 6, fontSize: 12 }}
                >
                  {src.label}
                </Button>
              </Tooltip>
            ))}
            <Divider type="vertical" style={{ height: 20 }} />
            <Button
              size="small"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{ borderRadius: 6, color: "#52c41a", borderColor: "#b7eb8f" }}
            >
              Export Excel
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeDataTab}
          onChange={setActiveDataTab}
          size="small"
          type="card"
          items={[
            {
              key: "all",
              label: (
                <span>
                  All Sources{" "}
                  <Badge
                    count={filteredRecords.length}
                    style={{ backgroundColor: "#1890ff", marginLeft: 4 }}
                    overflowCount={99999}
                    size="small"
                  />
                </span>
              ),
            },
            ...SOURCES.map((src) => ({
              key: src.key.toLowerCase().replace(/[^a-z0-9]/g, ""),
              label: (
                <span>
                  <Badge color={src.color} text="" style={{ marginRight: 4 }} />
                  {src.label}{" "}
                  <Badge
                    count={(displayRecords.filter((r) => r.source === src.key) || []).length}
                    style={{ backgroundColor: src.color, marginLeft: 4 }}
                    overflowCount={99999}
                    size="small"
                  />
                </span>
              ),
            })),
          ]}
        />

        {/* Source toggle checkboxes — only show on "All Sources" tab */}
        {activeDataTab === "all" && (
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ marginRight: 12 }}>
              Filter by Source:
            </Text>
            {SOURCES.map((src) => (
              <Checkbox
                key={src.key}
                checked={activeSourceFilters.includes(src.key)}
                onChange={() => handleSourceToggle(src.key)}
                style={{ marginRight: 8 }}
              >
                <Badge
                  color={src.color}
                  text={`${src.label} (${
                    (summary.bySource[src.key] || { count: 0 }).count
                  })`}
                />
              </Checkbox>
            ))}
            <Divider style={{ margin: "8px 0" }} />
          </div>
        )}

        {/* AG Grid — popupParent avoids filter menus clipping inside Card/Tabs; high z-index backup for popups */}
        <style>
          {`
            .sales-dashboard-daily-sales-grid .ag-popup,
            .sales-dashboard-daily-sales-grid .ag-menu,
            .sales-dashboard-daily-sales-grid .ag-filter-menu,
            .sales-dashboard-daily-sales-grid .ag-set-filter-list,
            .sales-dashboard-daily-sales-grid .ag-rich-select-list {
              z-index: 5000 !important;
            }
          `}
        </style>
        <div
          className="ag-theme-alpine sales-dashboard-daily-sales-grid"
          style={{ height: "calc(100vh - 200px)", minHeight: 500, width: "100%", position: "relative", zIndex: 1 }}
        >
          <AgGridWithColumnState
            tableId="sales-dashboard-daily-sales"
            gridApiRef={gridRef}
            popupParent={typeof document !== "undefined" ? document.body : undefined}
            rowData={tabFilteredRecords}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={100}
            paginationPageSizeSelector={[50, 100, 200, 500]}
            cellSelection={true}
            enableCellTextSelection={true}
            suppressMovableColumns={false}
            rowBuffer={30}
            animateRows={false}
            getRowId={(params) => params.data._id}
            sideBar={{
              toolPanels: [
                {
                  id: "columns",
                  labelDefault: "Columns",
                  labelKey: "columns",
                  iconKey: "columns",
                  toolPanel: "agColumnsToolPanel",
                },
                {
                  id: "filters",
                  labelDefault: "Filters",
                  labelKey: "filters",
                  iconKey: "filter",
                  toolPanel: "agFiltersToolPanel",
                },
              ],
            }}
          />
        </div>
      </Card>

      {/* ── Raw Data Explorer ── */}
      <Card
        title={
          <span style={{ fontSize: 15, fontWeight: 600 }}>
            <DatabaseOutlined style={{ marginRight: 8 }} />
            Raw Data Explorer
          </span>
        }
        size="small"
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "visible", marginTop: 16 }}
        styles={{ body: { overflow: "visible" } }}
        extra={
          <Space>
            <span style={{ fontSize: 12, color: "#999" }}>Platform:</span>
            <Select
              value={rawPlatform}
              onChange={setRawPlatform}
              style={{ width: 190 }}
              size="small"
              options={RAW_PLATFORMS}
            />
            <Badge
              count={rawDataRows.length}
              style={{ backgroundColor: "#1890ff" }}
              overflowCount={99999}
              size="small"
            />
            <Button
              size="small"
              icon={<FileExcelOutlined />}
              onClick={handleExportRawExcel}
              disabled={rawDataRows.length === 0}
              style={{ borderRadius: 6, color: "#52c41a", borderColor: "#b7eb8f" }}
            >
              Export Excel
            </Button>
          </Space>
        }
      >
        <style>
          {`
            .raw-data-explorer-grid .ag-popup,
            .raw-data-explorer-grid .ag-menu,
            .raw-data-explorer-grid .ag-filter-menu {
              z-index: 5000 !important;
            }
          `}
        </style>
        <div
          className="ag-theme-alpine raw-data-explorer-grid"
          style={{ height: 520, width: "100%", position: "relative", zIndex: 1 }}
        >
          {rawDataRows.length === 0 ? (
            <Empty
              description={
                rawPlatform === "NexRep"
                  ? "No NexRep sales for this date. Files must be in upload/nexrep/<brand>/ or upload/ with the brand in the filename, then Sync NexRep."
                  : `No raw data for ${rawPlatform} in the current selection`
              }
              style={{ marginTop: 80 }}
            />
          ) : (
            <AgGridWithColumnState
              tableId={`raw-data-explorer-${rawPlatform.replace(/[^a-zA-Z0-9]/g, "")}`}
              gridApiRef={rawGridRef}
              popupParent={typeof document !== "undefined" ? document.body : undefined}
              rowData={rawDataRows}
              columnDefs={rawColDefs}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              pagination={true}
              paginationPageSize={100}
              paginationPageSizeSelector={[50, 100, 200, 500]}
              cellSelection={true}
              enableCellTextSelection={true}
              animateRows={false}
              getRowId={undefined}
              sideBar={{
                toolPanels: [
                  { id: "columns", labelDefault: "Columns", labelKey: "columns", iconKey: "columns", toolPanel: "agColumnsToolPanel" },
                  { id: "filters", labelDefault: "Filters", labelKey: "filters", iconKey: "filter", toolPanel: "agFiltersToolPanel" },
                ],
              }}
            />
          )}
        </div>
      </Card>

      {/* ── Mail Order Tracking ── */}
      <Card
        title={
          <span style={{ fontSize: 15, fontWeight: 600 }}>
            <FileExcelOutlined style={{ marginRight: 8 }} />
            Mail Order Tracking
          </span>
        }
        size="small"
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "visible", marginTop: 16 }}
        styles={{ body: { overflow: "visible" } }}
        extra={
          <Space>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              beforeUpload={handleMailOrderUpload}
              disabled={mailOrderUploading}
            >
              <Button
                size="small"
                icon={<UploadOutlined />}
                loading={mailOrderUploading}
                type="primary"
                style={{ borderRadius: 6 }}
              >
                Upload Mail Orders
              </Button>
            </Upload>
            <Badge
              count={mailOrderData.length}
              style={{ backgroundColor: SOURCE_COLORS["Money Order"] }}
              overflowCount={99999}
              size="small"
            />
          </Space>
        }
      >
        <div
          className="ag-theme-alpine"
          style={{ height: 400, width: "100%", position: "relative", zIndex: 1 }}
        >
          {mailOrderData.length === 0 ? (
            <Empty
              image={<InboxOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />}
              description={
                <span style={{ color: "#999" }}>
                  No mail order data for the selected date range.
                  <br />Upload a Mail Orders Tracking file to populate this section.
                </span>
              }
              style={{ marginTop: 60 }}
            />
          ) : (
            <AgGridWithColumnState
              tableId="mail-order-tracking"
              gridApiRef={mailOrderGridRef}
              popupParent={typeof document !== "undefined" ? document.body : undefined}
              rowData={mailOrderData}
              columnDefs={MAIL_ORDER_COL_DEFS}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              pagination={true}
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100]}
              cellSelection={true}
              enableCellTextSelection={true}
              animateRows={false}
            />
          )}
        </div>
      </Card>

      {/* Elavon OTP Modal */}
      <Modal
        title="Elavon — Enter OTP"
        open={elavonOtpVisible}
        onCancel={handleElavonOtpCancel}
        footer={null}
        maskClosable
        destroyOnClose
        width={400}
      >
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          {elavonStatus === "waiting_for_otp" ? (
            <>
              <p style={{ marginBottom: 12, fontSize: 14 }}>
                Elavon requires a one-time verification code. Check your email/phone and enter the code below.
              </p>
              <Input
                size="large"
                placeholder="Enter OTP code"
                value={elavonOtpValue}
                onChange={(e) => setElavonOtpValue(e.target.value)}
                onPressEnter={handleElavonOtpSubmit}
                style={{ marginBottom: 16, maxWidth: 240, textAlign: "center", fontSize: 20, letterSpacing: 4 }}
                maxLength={10}
                autoFocus
              />
              <br />
              <Button
                type="primary"
                size="large"
                onClick={handleElavonOtpSubmit}
                loading={elavonSubmitting}
                disabled={!elavonOtpValue.trim()}
              >
                Submit OTP
              </Button>
            </>
          ) : elavonStatus === "submitting_otp" ? (
            <>
              <Spin size="large" />
              <p style={{ marginTop: 16, color: "#666" }}>Verifying OTP...</p>
            </>
          ) : (
            <>
              <Spin size="large" />
              <p style={{ marginTop: 16, color: "#666" }}>Processing...</p>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default DailySalesReport;
